import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type FormEvent,
  type PointerEvent as ReactPointerEvent,
  type WheelEvent,
} from 'react';
import { GlobalWorkerOptions, getDocument } from 'pdfjs-dist/legacy/build/pdf.mjs';
import { WorkerMessageHandler } from 'pdfjs-dist/legacy/build/pdf.worker.mjs';
import { useAuth } from '../../context/AuthContext';
import { listarParametrosPublicos } from '../../lib';
import MapaConductor from './MapaConductor';
import {
  createCargaGasolina,
  EMPTY_DASHBOARD_STATS,
  agregarCoordenada,
  firmarGuiaPdf,
  getCargasGasolina,
  getViajesAsignadosEnCursoConductor,
  getViajesAsignadosPendientesConductor,
  getConductorByUserId,
  getConductorDashboardStats,
  getViajesPaginados,
  saveViaje,
  subscribeStorage,
  uploadFileToMinio,
  type DashboardStatsData,
} from '../../lib/storage';
import type {
  CargaGasolina,
  Conductor,
  Coordenada,
  FormaPagoEntrega,
  FormaPagoCargaGasolina,
  GuiaDevolucionViaje,
  GuiaFirmadaViaje,
  GuiaPdfItemViaje,
  GuiaPdfViaje,
  Paquete,
  RegistroGasolina,
  Viaje,
} from '../../lib/appTypes';
import {
  ActivityFeed,
  DashboardStatCard,
  PanelShell,
  SectionSurface,
  WeeklyBarChart,
  type PanelNavItem,
} from '../../components/role-ui';
import { DescripcionResumida } from '../../components/DetalleEntrega';

type Paso = 1 | 2 | 3;
type Section = 'dashboard' | 'actual' | 'asignadas' | 'gasolina' | 'historial';
type FormaPagoSeleccion = '' | FormaPagoEntrega;
type AsignadasEstadoFiltro = 'todas' | 'pendiente' | 'en_curso';
type HistorialEstadoFiltro = 'todas' | 'finalizado' | 'devolucion';
type HistorialTipoFiltro = 'todas' | 'entrega' | 'transferencia' | 'envio';
type DevolucionesPorGuia = Record<number, string[]>;
type CameraUploadTarget = 'paquete' | 'entrega' | 'gasolina';
type BrowserNotificationPermission = NotificationPermission | 'unsupported';
type PdfSignatureInput = {
  signatureContent: string;
  pageNumber: number;
  pdfX: number;
  pdfY: number;
  pdfWidth: number;
  pdfHeight: number;
};
type StrokeBounds = { left: number; top: number; right: number; bottom: number };

const FORMAS_PAGO_ENTREGA = ['efectivo', 'transferencia', 'cheque', 'nota_credito'] as const;
const PAYMENT_REFERENCE_SEPARATOR = ' / ';
const HISTORIAL_PAGE_SIZE = 10;
const CONDUCTOR_SYNC_INTERVAL_MS = 8000;
const CONDUCTOR_ACTIVE_TRIP_SYNC_INTERVAL_MS = 5000;
const MAX_STORED_ASSIGNMENT_NOTIFICATION_IDS = 150;
const MOTIVOS_DEVOLUCION_ENTREGA = [
  'Producto defectuoso',
  'Productos duplicado',
  'producto faltante',
  'producto no aplicamodelo vehiculo',
  'producto problema de funcionamiento',
] as const;

if (typeof globalThis !== 'undefined') {
  (
    globalThis as {
      pdfjsWorker?: {
        WorkerMessageHandler: typeof WorkerMessageHandler;
      };
    }
  ).pdfjsWorker = {
    WorkerMessageHandler,
  };
}

const SECTION_COPY: Record<Section, { label: string; icon: string; detail: string }> = {
  dashboard: {
    label: 'Dashboard',
    icon: 'fa-solid fa-chart-line',
    detail: 'Resumen personal de viajes y actividad reciente.',
  },
  actual: {
    label: 'Viaje actual',
    icon: 'fa-solid fa-route',
    detail: 'Gestiona la entrega activa, recepcion del paquete, recorrido y cierre.',
  },
  asignadas: {
    label: 'Entregas asignadas',
    icon: 'fa-solid fa-box-open',
    detail: 'Consulta las entregas pendientes o en curso asignadas a tu usuario conductor.',
  },
  gasolina: {
    label: 'Carga de Gasolina',
    icon: 'fa-solid fa-gas-pump',
    detail: 'Registra cargas de gasolina por vehículo y consulta tu historial reciente.',
  },
  historial: {
    label: 'Historial',
    icon: 'fa-solid fa-clock-rotate-left',
    detail: 'Revisa viajes completados, documentos firmados y evidencias de entrega.',
  },
};

function createEmptyPaquete(): Paquete {
  return {
    nombre: '',
    descripcion: '',
    imagenes: [],
  };
}

function createEmptyGasolina(): RegistroGasolina {
  return {
    tipo: 'EXTRA',
    galones: 0,
    precioPorGalon: 0,
  };
}

function splitPaymentReference(value?: string): { valor: string; referencia: string } {
  if (!value) {
    return { valor: '', referencia: '' };
  }

  const [valor = '', ...rest] = value.split(PAYMENT_REFERENCE_SEPARATOR);
  if (rest.length === 0) {
    return { valor: value, referencia: '' };
  }

  return {
    valor,
    referencia: rest.join(PAYMENT_REFERENCE_SEPARATOR),
  };
}

function buildPaymentReference(
  formaPago: FormaPagoSeleccion,
  valorPago: string,
  referenciaPago: string,
): string {
  const valor = valorPago.trim();
  const referencia = referenciaPago.trim();

  if (formaPago === 'transferencia' || formaPago === 'cheque' || formaPago === 'nota_credito') {
    return `${valor}${PAYMENT_REFERENCE_SEPARATOR}${referencia}`;
  }

  return valor;
}

function formatCurrency(value?: number): string {
  if (typeof value !== 'number' || Number.isNaN(value)) return '—';
  return new Intl.NumberFormat('es-EC', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

function formatPaymentLabel(tipo?: string): string {
  switch (tipo) {
    case 'efectivo':
      return 'Efectivo';
    case 'transferencia':
      return 'Transferencia';
    case 'cheque':
      return 'Cheque';
    case 'nota_credito':
      return 'Nota de crédito';
    case 'tc':
      return 'TC';
    case 'parcial':
      return 'Por cobrar';
    case 'completo':
      return 'Pagado';
    default:
      return '—';
  }
}

function formatDeliveryTypeLabel(tipo?: Viaje['tipoEntrega'] | string): string {
  switch (tipo) {
    case 'envio':
      return 'Envío';
    case 'transferencia':
      return 'Transferencia';
    case 'entrega':
      return 'Entrega';
    default:
      return '—';
  }
}

function canUseAssignmentNotifications(): boolean {
  if (typeof window === 'undefined' || !('Notification' in window)) return false;
  const isLocalhost = ['localhost', '127.0.0.1'].includes(window.location.hostname);
  return window.isSecureContext || isLocalhost;
}

function getInitialNotificationPermission(): BrowserNotificationPermission {
  if (!canUseAssignmentNotifications()) return 'unsupported';
  return Notification.permission;
}

function isLikelyMobileNotificationDevice(): boolean {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
  const userAgent = navigator.userAgent || '';
  if (/Android|iPhone|iPad|iPod|Mobile/i.test(userAgent)) return true;
  return window.matchMedia?.('(pointer: coarse)').matches ?? false;
}

function getAssignedTripsForConductor(viajes: Viaje[], conductorId?: string): Viaje[] {
  if (!conductorId) return [];
  return viajes.filter(
    viaje => viaje.estado !== 'finalizado'
      && (viaje.conductorId === conductorId || viaje.secondaryConductorId === conductorId),
  );
}

function buildAssignedTripNotificationId(viaje: Viaje, conductorId: string): string {
  return `${conductorId}:${viaje.id}`;
}

function getAssignmentNotificationStorageKey(conductorId: string): string {
  return `freisacar.conductor.assignment.notifications.${conductorId}`;
}

function readStoredAssignmentNotificationIds(storageKey: string): Set<string> {
  if (typeof window === 'undefined') return new Set<string>();

  try {
    const rawValue = window.localStorage.getItem(storageKey);
    if (!rawValue) return new Set<string>();
    const parsed = JSON.parse(rawValue);
    if (!Array.isArray(parsed)) return new Set<string>();
    return new Set(parsed.filter((value): value is string => typeof value === 'string'));
  } catch {
    return new Set<string>();
  }
}

function persistAssignmentNotificationIds(storageKey: string, ids: Set<string>): void {
  if (typeof window === 'undefined') return;

  try {
    window.localStorage.setItem(
      storageKey,
      JSON.stringify(Array.from(ids).slice(-MAX_STORED_ASSIGNMENT_NOTIFICATION_IDS)),
    );
  } catch {
    return;
  }
}

function buildAssignedTripNotificationCopy(viaje: Viaje): { title: string; body: string } {
  const deliveryLabel = formatDeliveryTypeLabel(viaje.tipoEntrega);
  const clienteNombre = viaje.paquete?.cliente?.nombre?.trim() || 'Cliente sin nombre';
  const numeroDocumento = viaje.paquete?.cliente?.numero?.trim();
  const descripcion = viaje.paquete?.nombre?.trim();
  const bodyParts = [clienteNombre];

  if (descripcion && descripcion !== clienteNombre) {
    bodyParts.push(descripcion);
  }
  if (numeroDocumento) {
    bodyParts.push(`Documento ${numeroDocumento}`);
  }

  return {
    title: `Nueva asignación de ${deliveryLabel.toLowerCase()}`,
    body: bodyParts.join(' · '),
  };
}

async function showAssignedTripNotification(viaje: Viaje): Promise<void> {
  if (!canUseAssignmentNotifications() || Notification.permission !== 'granted') return;

  const { title, body } = buildAssignedTripNotificationCopy(viaje);
  const options: NotificationOptions = {
    body,
    icon: '/icons/app-icon.svg',
    tag: `viaje-asignado-${viaje.id}`,
    data: {
      path: '/',
      viajeId: viaje.id,
      section: 'asignadas',
    },
  };

  if ('serviceWorker' in navigator) {
    try {
      const registration = await navigator.serviceWorker.ready;
      await registration.showNotification(title, options);
      return;
    } catch {
      // Fallback below when the service worker is not yet ready.
    }
  }

  try {
    new Notification(title, options);
  } catch {
    return;
  }
}

function getHistoryTypeLabel(viaje: Viaje): string {
  return formatDeliveryTypeLabel(viaje.tipoEntrega);
}

function getHistoryDestinationLabel(viaje: Viaje): string {
  return viaje.urlDestino?.trim() || '—';
}

function getAssignedTripTone(tipo?: Viaje['tipoEntrega'] | string): {
  row: string;
  chip: string;
  dot: string;
  link: string;
} {
  switch (tipo) {
    case 'envio':
      return {
        row: 'bg-red-50/85',
        chip: 'border border-red-200 bg-red-100 text-red-800',
        dot: 'bg-red-300',
        link: 'text-red-700 hover:text-red-900',
      };
    case 'transferencia':
      return {
        row: 'bg-amber-50/85',
        chip: 'border border-amber-200 bg-amber-100 text-amber-800',
        dot: 'bg-amber-300',
        link: 'text-amber-700 hover:text-amber-900',
      };
    case 'entrega':
    default:
      return {
        row: 'bg-emerald-50/85',
        chip: 'border border-emerald-200 bg-emerald-100 text-emerald-800',
        dot: 'bg-emerald-300',
        link: 'text-emerald-700 hover:text-emerald-900',
      };
  }
}

function formatAmountInput(value?: number): string {
  if (typeof value !== 'number' || Number.isNaN(value)) return '';
  return value.toFixed(2).replace('.', ',');
}

function parseAmountInput(value: string): number | undefined {
  const normalized = value.trim().replace(',', '.');
  if (!normalized) return undefined;
  const parsed = Number(normalized);
  if (!Number.isFinite(parsed)) return undefined;
  return Number(parsed.toFixed(2));
}

function preventNumberInputWheelChange(event: WheelEvent<HTMLInputElement>): void {
  event.currentTarget.blur();
}

function isValidPaymentAmount(value: string): boolean {
  const normalized = value.trim();
  return normalized.length > 0
    && normalized.length <= 12
    && /^[0-9]+(?:,[0-9]{1,2})?$/.test(normalized);
}

function isValidPaymentReference(value: string): boolean {
  const normalized = value.trim();
  return normalized.length > 0
    && normalized.length <= 40
    && /^[A-Za-z0-9-]+$/.test(normalized);
}

function getInvoiceTotal(paquete?: Paquete): number | undefined {
  if (!paquete) return undefined;
  if (typeof paquete.valorTotalFactura === 'number') return paquete.valorTotalFactura;

  const pago = paquete.pago;
  if (!pago) return undefined;
  if (typeof pago.monto === 'number') return pago.monto;
  if (typeof pago.valorNotaCredito === 'number') return pago.valorNotaCredito;
  return undefined;
}

function getInitialPartialAmount(paquete?: Paquete): number {
  if (paquete?.pago?.tipo !== 'parcial') return 0;
  return typeof paquete.pago.montoPagado === 'number' ? paquete.pago.montoPagado : 0;
}

function getPendingAmount(paquete?: Paquete): number | undefined {
  const total = getInvoiceTotal(paquete);
  if (typeof total !== 'number') return undefined;

  if (paquete?.pago?.tipo === 'parcial') {
    return Number(Math.max(total - getInitialPartialAmount(paquete), 0).toFixed(2));
  }

  return Number(total.toFixed(2));
}

function getConfiguredPaymentAmount(paquete?: Paquete): number | undefined {
  const pago = paquete?.pago;
  if (!pago) return undefined;

  if (pago.tipo === 'parcial') {
    return getPendingAmount(paquete);
  }
  if (pago.tipo === 'nota_credito') {
    return typeof pago.valorNotaCredito === 'number' ? pago.valorNotaCredito : undefined;
  }
  // La transferencia pactada (p. ej. desde el ERP) no trae monto: se cobra el total de la factura.
  if (pago.tipo === 'transferencia' && typeof pago.monto !== 'number') {
    return getInvoiceTotal(paquete);
  }

  return typeof pago.monto === 'number' ? pago.monto : undefined;
}

function formatDescripcionPorFactura(descripcion?: string): string {
  const normalizedDescription = descripcion?.trim();
  if (!normalizedDescription) return '—';
  if (!normalizedDescription.includes('Factura:')) return normalizedDescription;

  return normalizedDescription
    .split(/(?=Factura:)/)
    .map(section => section.trim())
    .filter(Boolean)
    .join('\n\n');
}

function getGuiasPdfViaje(viaje?: Viaje | null): GuiaPdfViaje[] {
  const guias = viaje?.paquete?.guiasPdf?.filter(guia => guia.url) ?? [];
  if (guias.length > 0) return guias;

  if (viaje?.paquete?.guiaPdfUrl) {
    return [{
      url: viaje.paquete.guiaPdfUrl,
      nombreArchivo: viaje.paquete.guiaPdfNombreArchivo,
      numeroFactura: viaje.paquete.cliente?.numero,
    }];
  }

  return [];
}

function extractInvoiceNumberFromGuideName(nombreArchivo?: string): string | undefined {
  const normalizedName = nombreArchivo?.trim();
  if (!normalizedName) return undefined;

  const withoutExtension = normalizedName.replace(/\.pdf$/i, '').trim();
  const facturaMatch = withoutExtension.match(/^factura[\s:_-]+(.+)$/i);
  return facturaMatch?.[1]?.trim() || undefined;
}

function getViajeInvoiceNumbers(viaje?: Viaje | null): string[] {
  if (!viaje) return [];

  const guiaNumbers = getGuiasPdfViaje(viaje)
    .map(guia => guia.numeroFactura?.trim() || extractInvoiceNumberFromGuideName(guia.nombreArchivo))
    .filter((numero): numero is string => Boolean(numero));

  if (guiaNumbers.length > 0) return guiaNumbers;

  const clienteNumero = viaje.paquete?.cliente?.numero?.trim();
  return clienteNumero ? [clienteNumero] : [];
}

function getGuiasFirmadasViaje(viaje?: Viaje | null): GuiaFirmadaViaje[] {
  const guias = viaje?.guiasFirmadas?.filter(guia => guia.url) ?? [];
  if (guias.length > 0) return guias;

  if (viaje?.pdfGuiaFirmadoUrl) {
    return [{ url: viaje.pdfGuiaFirmadoUrl }];
  }

  return [];
}

function getGuiasVisiblesHistorialViaje(viaje?: Viaje | null): Array<{ url: string; nombreArchivo?: string; firmada: boolean }> {
  const guiasFirmadas = getGuiasFirmadasViaje(viaje);
  if (guiasFirmadas.length > 0) {
    return guiasFirmadas.map(guia => ({
      url: guia.url,
      nombreArchivo: guia.nombreArchivo,
      firmada: true,
    }));
  }

  return getGuiasPdfViaje(viaje).map(guia => ({
    url: guia.url,
    nombreArchivo: guia.nombreArchivo,
    firmada: false,
  }));
}

function getEnvioPdfViaje(viaje?: Viaje | null): { url: string; nombreArchivo?: string } | null {
  const url = viaje?.paquete?.envioPdfUrl?.trim();
  if (!url) return null;

  return {
    url,
    nombreArchivo: viaje?.paquete?.envioPdfNombreArchivo,
  };
}

function getReturnedGuideIndices(viaje?: Viaje | null): number[] {
  return getGuiasPdfViaje(viaje).reduce<number[]>((indices, guia, index) => {
    if (guia.esDevuelta) indices.push(index);
    return indices;
  }, []);
}

function getGuiaItemKey(item: GuiaPdfItemViaje): string {
  const codigo = item.codigo.trim();
  const descripcion = item.descripcion.trim().toLowerCase();
  if (codigo && descripcion) return `${codigo}|${descripcion}`;
  if (codigo) return `codigo:${codigo}`;
  if (descripcion) return `descripcion:${descripcion}`;
  return '';
}

function getGuiaItems(guia: GuiaPdfViaje): GuiaPdfItemViaje[] {
  return guia.extraccion?.items ?? [];
}

function getGuiaTotal(guia: GuiaPdfViaje): number {
  if (typeof guia.valorTotal === 'number') return guia.valorTotal;
  if (typeof guia.extraccion?.total === 'number') return guia.extraccion.total;
  return Number(
    getGuiaItems(guia)
      .reduce((total, item) => total + (typeof item.precioTotal === 'number' ? item.precioTotal : 0), 0)
      .toFixed(2),
  );
}

function getReturnedGuideItemSelections(viaje?: Viaje | null): DevolucionesPorGuia {
  return getGuiasPdfViaje(viaje).reduce<DevolucionesPorGuia>((acc, guia, index) => {
    const returnedItemKeys = (guia.itemsDevueltos ?? [])
      .map(getGuiaItemKey)
      .filter(Boolean);
    if (returnedItemKeys.length > 0 && !guia.esDevuelta) {
      acc[index] = returnedItemKeys;
    }
    return acc;
  }, {});
}

function getGuideReturnedAmount(
  guia: GuiaPdfViaje,
  index: number,
  fullReturnedGuideIndexes: Set<number>,
  returnedItemsByGuide: DevolucionesPorGuia,
): number {
  if (fullReturnedGuideIndexes.has(index)) {
    return Number(getGuiaTotal(guia).toFixed(2));
  }

  const selectedKeys = new Set(returnedItemsByGuide[index] ?? []);
  if (selectedKeys.size === 0) return 0;

  return Number(
    getGuiaItems(guia)
      .reduce((total, item) => {
        if (!selectedKeys.has(getGuiaItemKey(item))) return total;
        return total + (typeof item.precioTotal === 'number' ? item.precioTotal : 0);
      }, 0)
      .toFixed(2),
  );
}

function buildGuideReturnPayload(
  guides: GuiaPdfViaje[],
  fullReturnedGuideIndexes: Set<number>,
  returnedItemsByGuide: DevolucionesPorGuia,
): GuiaDevolucionViaje[] {
  return guides.reduce<GuiaDevolucionViaje[]>((acc, guia, index) => {
    if (fullReturnedGuideIndexes.has(index)) {
      acc.push({
        url: guia.url,
        nombreArchivo: guia.nombreArchivo,
        numeroFactura: guia.numeroFactura,
        direccion: guia.direccion,
        valorTotal: guia.valorTotal,
        esDevuelta: true,
        devolucionCompleta: true,
        itemsDevueltos: getGuiaItems(guia),
      });
      return acc;
    }

    const selectedKeys = new Set(returnedItemsByGuide[index] ?? []);
    if (selectedKeys.size === 0) return acc;

    acc.push({
      url: guia.url,
      nombreArchivo: guia.nombreArchivo,
      numeroFactura: guia.numeroFactura,
      direccion: guia.direccion,
      valorTotal: guia.valorTotal,
      esDevuelta: false,
      devolucionCompleta: false,
      itemsDevueltos: getGuiaItems(guia).filter(item => selectedKeys.has(getGuiaItemKey(item))),
    });
    return acc;
  }, []);
}

function buildReturnObservations(
  guides: GuiaPdfViaje[],
  fullReturnedGuideIndexes: Set<number>,
  returnedItemsByGuide: DevolucionesPorGuia,
): string {
  const returnedEntries = guides.reduce<string[]>((entries, guia, index) => {
    const guideLabel = guia.numeroFactura?.trim() ? `Factura ${guia.numeroFactura.trim()}` : `Guía ${index + 1}`;

    if (fullReturnedGuideIndexes.has(index)) {
      const guideItems = getGuiaItems(guia);
      if (guideItems.length > 0) {
        entries.push(
          `${guideLabel}: ${guideItems.map(item => `${item.descripcion} (${formatCurrency(item.precioTotal ?? undefined)})`).join(', ')}`,
        );
      } else {
        entries.push(`${guideLabel}: ${formatCurrency(getGuiaTotal(guia))}`);
      }

      return entries;
    }

    const selectedKeys = new Set(returnedItemsByGuide[index] ?? []);
    if (selectedKeys.size === 0) return entries;

    const selectedItems = getGuiaItems(guia)
      .filter(item => selectedKeys.has(getGuiaItemKey(item)))
      .map(item => `${item.descripcion} (${formatCurrency(item.precioTotal ?? undefined)})`);

    if (selectedItems.length > 0) {
      entries.push(`${guideLabel}: ${selectedItems.join(', ')}`);
    }

    return entries;
  }, []);

  if (returnedEntries.length === 0) return '';

  const returnedTotal = guides.reduce(
    (total, guia, index) => total + getGuideReturnedAmount(guia, index, fullReturnedGuideIndexes, returnedItemsByGuide),
    0,
  );

  return `Productos devueltos: ${returnedEntries.join(', ')}. Total devuelto: ${formatCurrency(returnedTotal)}.`;
}

function buildAutoReturnObservations(reason: string, returnedItemsDetail: string): string {
  const normalizedReason = reason.trim();
  const normalizedReturnedItemsDetail = returnedItemsDetail.trim();

  if (!normalizedReason) return normalizedReturnedItemsDetail;
  if (!normalizedReturnedItemsDetail) return `Motivo de devolución: ${normalizedReason}.`;
  return `Motivo de devolución: ${normalizedReason}.\n\n${normalizedReturnedItemsDetail}`;
}

function mergeAutoReturnObservations(currentText: string, nextAutoText: string, previousAutoText: string): string {
  const normalizedPreviousAuto = previousAutoText.trim();

  if (!normalizedPreviousAuto) {
    return nextAutoText ? (currentText ? `${nextAutoText}\n\n${currentText}` : nextAutoText) : currentText;
  }

  if (currentText.startsWith(`${previousAutoText}\n\n`)) {
    const manualText = currentText.slice(previousAutoText.length + 2);
    return manualText ? `${nextAutoText}\n\n${manualText}` : nextAutoText;
  }

  if (currentText.startsWith(previousAutoText)) {
    const manualText = currentText.slice(previousAutoText.length);
    return manualText ? `${nextAutoText}\n\n${manualText}` : nextAutoText;
  }

  return currentText.trim() ? `${nextAutoText}\n\n${currentText}` : nextAutoText;
}

function stripAutoReturnObservations(currentText: string, previousAutoText: string, fallbackText = ''): string {
  const normalizedCurrent = currentText.trim();
  const normalizedPreviousAuto = previousAutoText.trim();
  const normalizedFallback = fallbackText.trim();

  if (!normalizedPreviousAuto) return normalizedCurrent || normalizedFallback;
  if (normalizedCurrent === normalizedPreviousAuto) return normalizedFallback;
  if (normalizedCurrent.startsWith(`${normalizedPreviousAuto}\n\n`)) {
    return normalizedCurrent.slice(normalizedPreviousAuto.length + 2).trim();
  }
  if (normalizedCurrent.startsWith(normalizedPreviousAuto)) {
    return normalizedCurrent.slice(normalizedPreviousAuto.length).trim();
  }
  return normalizedCurrent || normalizedFallback;
}

type GuiaItemsDetallePanelProps = {
  guia: GuiaPdfViaje;
  guiaIndex: number;
  guiaFirmadaUrl?: string;
  devolucionActiva: boolean;
  guiaMarcadaComoDevuelta: boolean;
  selectedItemKeys: Set<string>;
  onToggleGuiaDevolucion: () => void;
  onToggleItemDevolucion: (itemKey: string) => void;
};

function GuiaItemsDetallePanel({
  guia,
  guiaIndex,
  guiaFirmadaUrl,
  devolucionActiva,
  guiaMarcadaComoDevuelta,
  selectedItemKeys,
  onToggleGuiaDevolucion,
  onToggleItemDevolucion,
}: GuiaItemsDetallePanelProps) {
  const guiaItems = getGuiaItems(guia);
  const guiaBaseKey = guia.url || guia.numeroFactura || guia.nombreArchivo || `guia-${guiaIndex + 1}`;

  return (
    <div className={`mt-3 space-y-3 rounded-2xl border border-stone-200 p-3 ${devolucionActiva ? 'bg-stone-50/80' : 'bg-white/80'}`}>
      {devolucionActiva && (
        <label className="flex items-center gap-2 text-xs font-semibold text-stone-700">
          <input
            type="checkbox"
            checked={guiaMarcadaComoDevuelta}
            onChange={onToggleGuiaDevolucion}
            className="h-4 w-4 rounded border-stone-300 text-red-600 focus:ring-red-200"
          />
          Marcar guía completa como devuelta
        </label>
      )}
      <div className="space-y-2">
        <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-stone-500">
          {devolucionActiva ? 'Detalle de factura y productos a devolver' : 'Detalle de factura'}
        </p>
        {guiaFirmadaUrl && devolucionActiva && (
          <p className="text-xs text-stone-500">
            La guía ya está firmada. Aun así puedes marcar devolución total o parcial; el sistema descontará lo devuelto y mantendrá la firma registrada.
          </p>
        )}
        {guiaItems.length > 0 ? (
          guiaItems.map(item => {
            const itemKey = getGuiaItemKey(item);
            const itemSelected = selectedItemKeys.has(itemKey);
            const itemContent = (
              <span className="flex-1">
                <strong className="text-stone-900">{item.descripcion}</strong>
                <span className="block text-stone-500">Código: {item.codigo || '—'}</span>
                <span className="block text-stone-500">Cantidad: {item.cantidad ?? '—'} | Precio total: {formatCurrency(item.precioTotal ?? undefined)}</span>
              </span>
            );

            if (!devolucionActiva) {
              return (
                <div key={`${guiaBaseKey}-${itemKey}`} className="rounded-2xl border border-stone-200 bg-stone-50 px-3 py-2 text-xs text-stone-700">
                  {itemContent}
                </div>
              );
            }

            return (
              <label key={`${guiaBaseKey}-${itemKey}`} className="flex items-start gap-3 rounded-2xl border border-stone-200 bg-white px-3 py-2 text-xs text-stone-700">
                <input
                  type="checkbox"
                  checked={itemSelected}
                  onChange={() => onToggleItemDevolucion(itemKey)}
                  disabled={guiaMarcadaComoDevuelta}
                  className="mt-0.5 h-4 w-4 rounded border-stone-300 text-red-600 focus:ring-red-200"
                />
                {itemContent}
              </label>
            );
          })
        ) : (
          <p className="rounded-2xl border border-dashed border-stone-200 bg-white px-3 py-2 text-xs text-stone-500">
            No se encontró el detalle de ítems para esta factura.
          </p>
        )}
      </div>
    </div>
  );
}

function hasSignedRequiredGuides(viaje?: Viaje | null, skippedGuideIndexes?: Set<number>): boolean {
  const guias = getGuiasPdfViaje(viaje);
  if (guias.length === 0) return false;

  const signedGuides = getGuiasFirmadasViaje(viaje);
  return guias.every((_, index) => skippedGuideIndexes?.has(index) || Boolean(signedGuides[index]?.url));
}

function getAssignedPaymentDetails(viaje?: Viaje): string[] {
  if (viaje?.tipoEntrega === 'transferencia') return [];

  const paquete = viaje?.paquete;
  const pago = paquete?.pago;
  if (!pago) return [];

  if (pago.tipo === 'completo') {
    return viaje?.tipoEntrega === 'envio'
      ? ['La factura ya está pagada. Solo registra el valor del envío.']
      : ['Esta entrega ya está pagada. No debes cobrar nada al cliente.'];
  }

  if (pago.tipo === 'parcial') {
    const saldo = getPendingAmount(paquete);
    const medioSaldo = pago.tipoPagoParcial ? ` por ${formatPaymentLabel(pago.tipoPagoParcial).toLowerCase()}` : '';
    if (typeof saldo === 'number') {
      return [`La forma de pago pactada es Por cobrar. Debes cobrar ${formatCurrency(saldo)}${medioSaldo}.`];
    }
    return [`La forma de pago pactada es Por cobrar${medioSaldo}.`];
  }

  const montoPactado = getConfiguredPaymentAmount(paquete);
  if (typeof montoPactado === 'number') {
    return [`La forma de pago pactada es ${formatPaymentLabel(pago.tipo)} por un total de ${formatCurrency(montoPactado)}.`];
  }

  return [`La forma de pago pactada es ${formatPaymentLabel(pago.tipo)}.`];
}

function getPhoneDigits(value?: string | null): string {
  return (value || '').replace(/\D/g, '');
}

function getDialablePhone(value?: string | null): string | null {
  const digits = getPhoneDigits(value);
  return digits || null;
}

function getWhatsAppPhone(value?: string | null): string | null {
  const digits = getPhoneDigits(value);
  if (!digits) return null;
  if (digits.startsWith('593')) return digits;
  if (digits.startsWith('0') && digits.length >= 8 && digits.length <= 10) return `593${digits.slice(1)}`;
  if (digits.length === 9) return `593${digits}`;
  return digits;
}

async function openPhoneDialer(value?: string | null): Promise<void> {
  const dialablePhone = getDialablePhone(value);
  if (!dialablePhone) return;

  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(dialablePhone);
    } catch {
      // Ignora fallos del portapapeles y abre igual el marcador.
    }
  }

  if (typeof window !== 'undefined') {
    window.location.href = `tel:${dialablePhone}`;
  }
}

function ClienteContactoResumen({ viaje, className = '' }: { viaje?: Viaje | null; className?: string }) {
  if (viaje?.tipoEntrega === 'transferencia') return null;

  const cliente = viaje?.paquete?.cliente;
  const direccion = cliente?.direccion || viaje?.direccionEntrega || cliente?.ciudad || '—';
  const telefonoCliente = cliente?.telefono?.trim() || '';
  const telefono = telefonoCliente || '—';
  const whatsappPhone = getWhatsAppPhone(telefonoCliente);
  const dialablePhone = getDialablePhone(telefonoCliente);

  return (
    <div className={`grid gap-2 text-sm text-stone-600 md:grid-cols-2 ${className}`}>
      <p><strong>Cliente:</strong> {cliente?.nombre || '—'}</p>
      <p><strong>Correo:</strong> {cliente?.correoElectronico || '—'}</p>
      <p><strong>Dirección:</strong> {direccion}</p>
      <p className="flex flex-wrap items-center gap-2 md:col-span-2">
        <strong>Teléfono:</strong>
        <span>{telefono}</span>
        {whatsappPhone && (
          <a
            href={`https://wa.me/${whatsappPhone}`}
            target="_blank"
            rel="noreferrer"
            aria-label="Abrir WhatsApp del cliente"
            title="Abrir WhatsApp del cliente"
            className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-emerald-200 bg-emerald-50 text-emerald-700 transition hover:bg-emerald-100"
          >
            <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current" aria-hidden="true">
              <path d="M19.05 4.91A9.82 9.82 0 0 0 12.03 2C6.62 2 2.21 6.4 2.21 11.82c0 1.73.45 3.42 1.31 4.9L2 22l5.44-1.43a9.8 9.8 0 0 0 4.58 1.16h.01c5.41 0 9.82-4.41 9.82-9.82a9.75 9.75 0 0 0-2.8-7ZM12.03 20.07h-.01a8.1 8.1 0 0 1-4.13-1.13l-.3-.18-3.23.85.86-3.15-.2-.32a8.12 8.12 0 0 1-1.25-4.32c0-4.5 3.67-8.17 8.18-8.17 2.18 0 4.22.85 5.77 2.39a8.08 8.08 0 0 1 2.39 5.78c0 4.5-3.67 8.17-8.18 8.17Zm4.48-6.13c-.24-.12-1.41-.69-1.63-.76-.22-.08-.38-.12-.54.12-.16.24-.62.76-.76.92-.14.16-.28.18-.52.06-.24-.12-1-.37-1.91-1.18-.71-.63-1.19-1.41-1.33-1.65-.14-.24-.01-.37.11-.49.11-.11.24-.28.36-.42.12-.14.16-.24.24-.4.08-.16.04-.3-.02-.42-.06-.12-.54-1.29-.74-1.77-.2-.47-.4-.41-.54-.42h-.46c-.16 0-.42.06-.64.3-.22.24-.84.82-.84 2s.86 2.31.98 2.47c.12.16 1.69 2.58 4.11 3.62.57.25 1.02.4 1.37.51.58.18 1.11.15 1.52.09.46-.07 1.41-.58 1.61-1.14.2-.56.2-1.04.14-1.14-.06-.1-.22-.16-.46-.28Z" />
            </svg>
          </a>
        )}
        {dialablePhone && (
          <button
            type="button"
            onClick={() => {
              void openPhoneDialer(telefonoCliente);
            }}
            aria-label="Abrir aplicación de teléfono y copiar número"
            title="Abrir aplicación de teléfono y copiar número"
            className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-sky-200 bg-sky-50 text-sky-700 transition hover:bg-sky-100"
          >
            <svg viewBox="0 0 24 24" className="h-4 w-4 stroke-current" aria-hidden="true" fill="none" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.12 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.12.89.33 1.76.63 2.6a2 2 0 0 1-.45 2.11L8.02 9.7a16 16 0 0 0 6.28 6.28l1.27-1.27a2 2 0 0 1 2.11-.45c.84.3 1.71.51 2.6.63A2 2 0 0 1 22 16.92Z" />
            </svg>
          </button>
        )}
      </p>
    </div>
  );
}

function GuiaCobroResumen({
  viaje,
  className = '',
  separarDescripcionFacturas = false,
}: {
  viaje?: Viaje | null;
  className?: string;
  separarDescripcionFacturas?: boolean;
}) {
  if (!viaje) return null;
  const descripcion = separarDescripcionFacturas
    ? formatDescripcionPorFactura(viaje.paquete?.descripcion)
    : viaje.paquete?.descripcion || '—';

  return (
    <div className={`grid gap-3 md:grid-cols-2 ${className}`}>
      <div className="rounded-2xl bg-white/80 p-3">
        <p className="text-xs uppercase tracking-[0.16em] text-stone-500">Tipo de entrega</p>
        <p className="mt-2 text-base font-semibold text-stone-900">{formatDeliveryTypeLabel(viaje.tipoEntrega)}</p>
      </div>
      <div className="rounded-2xl bg-white/80 p-3">
        <p className="text-xs uppercase tracking-[0.16em] text-stone-500">Descripción</p>
        <DescripcionResumida viaje={viaje} texto={descripcion} className="mt-2 text-sm font-semibold text-stone-900" />
      </div>
      {viaje.tipoEntrega === 'envio' && (
        <div className="rounded-2xl bg-amber-50/80 p-3">
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-amber-800">Compañía de transporte</p>
          <p className="mt-2 text-base font-bold text-stone-900">{viaje.companiaTransporte || '—'}</p>
        </div>
      )}
      <div className="rounded-2xl bg-white/80 p-3">
        <p className="text-xs uppercase tracking-[0.16em] text-stone-500">Valor total factura</p>
        <p className="mt-2 text-base font-semibold text-stone-900">{formatCurrency(getInvoiceTotal(viaje.paquete))}</p>
      </div>
    </div>
  );
}

function getAssignedPaymentHint(viaje?: Viaje): string {
  if (viaje?.tipoEntrega === 'transferencia') {
    return 'Esta transferencia no requiere forma de pago ni comprobante.';
  }
  return getAssignedPaymentDetails(viaje)[0] ?? 'No hay una forma de pago pactada registrada para esta entrega.';
}

function getFinalPaymentSummary(viaje: Viaje): string[] {
  if (viaje.tipoEntrega === 'transferencia') {
    return ['Transferencia completada sin forma de pago ni comprobante.'];
  }

  if (viaje.esDevolucion) {
    if (viaje.tipoDevolucion === 'parcial') {
      return [
        'Entrega marcada como devolución parcial.',
        `Monto registrado: ${formatCurrency(viaje.montoCobroViaje ?? 0)}`,
        'Se registró solo el cobro de las guías firmadas.',
      ];
    }

    return [
      'Entrega marcada como devolución.',
      `Monto registrado: ${formatCurrency(0)}`,
      'No se registró forma de pago ni comprobante.',
    ];
  }

  const pagoEntrega = viaje.pagoEntrega;
  if (!pagoEntrega) {
    const fallback = [`Forma de pago: ${viaje.formaPago ? formatPaymentLabel(viaje.formaPago) : '—'}`];
    if (viaje.referenciaPago) fallback.push(`Referencia: ${viaje.referenciaPago}`);
    return fallback;
  }

  const amount = pagoEntrega.tipo === 'nota_credito' ? pagoEntrega.valorNotaCredito : pagoEntrega.monto;
  const details = [`Forma de pago: ${formatPaymentLabel(pagoEntrega.tipo)}`];
  if (typeof amount === 'number') {
    details.push(`Monto registrado: ${formatCurrency(amount)}`);
  }
  if (pagoEntrega.banco) details.push(`Banco: ${pagoEntrega.banco}`);
  if (pagoEntrega.numeroTransferencia) details.push(`Transferencia: ${pagoEntrega.numeroTransferencia}`);
  if (pagoEntrega.numeroCheque) details.push(`Cheque: ${pagoEntrega.numeroCheque}`);
  if (pagoEntrega.numeroNotaCredito) details.push(`Nota de crédito: ${pagoEntrega.numeroNotaCredito}`);
  return details;
}

function getExternalMapUrl(viaje?: Viaje | null): string {
  const directUrl = (viaje?.urlDestino || '').trim();
  if (directUrl) return directUrl;

  const destination = viaje?.coordenadaFin;
  if (destination) {
    return `https://www.google.com/maps?q=${destination.lat},${destination.lng}`;
  }

  return '';
}

function sortViajes(left: Viaje, right: Viaje): number {
  const leftDate = left.fechaFin ?? left.fechaInicio ?? 0;
  const rightDate = right.fechaFin ?? right.fechaInicio ?? 0;
  return rightDate - leftDate;
}

function getPasoViaje(viaje?: Viaje | null, currentStep?: Paso): Paso {
  if (!viaje) return 1;
  if (currentStep === 3 && viaje.estado === 'en_curso') return 3;
  if (viaje.estado === 'pendiente') return 1;
  return viaje.tipoEntrega === 'transferencia' ? 3 : 2;
}

function upsertViaje(viajes: Viaje[], nextViaje: Viaje): Viaje[] {
  const index = viajes.findIndex(viaje => viaje.id === nextViaje.id);
  if (index === -1) {
    return [nextViaje, ...viajes].sort(sortViajes);
  }

  const next = [...viajes];
  next[index] = nextViaje;
  return next.sort(sortViajes);
}

function upsertViajeOperativo(viajes: Viaje[], nextViaje: Viaje): Viaje[] {
  if (nextViaje.estado === 'finalizado') {
    return viajes.filter(viaje => viaje.id !== nextViaje.id).sort(sortViajes);
  }

  return upsertViaje(viajes, nextViaje);
}

function estadoLabel(viaje: Viaje): string {
  if (viaje.estado === 'en_curso') return 'En camino';
  if (viaje.estado === 'finalizado') return 'Finalizado';
  return 'Pendiente';
}

function estadoBadge(viaje: Viaje): string {
  if (viaje.estado === 'en_curso') return 'border border-blue-200 bg-blue-50 text-blue-700';
  if (viaje.estado === 'finalizado') return 'border border-emerald-200 bg-emerald-50 text-emerald-700';
  return 'border border-amber-200 bg-amber-50 text-amber-800';
}

function getHistoryStatusLabel(viaje: Viaje): string {
  if (viaje.esDevolucion && viaje.tipoDevolucion === 'parcial') return 'Devolución parcial';
  if (viaje.esDevolucion) return 'Devolución';
  return estadoLabel(viaje);
}

function getHistoryStatusBadge(viaje: Viaje): string {
  if (viaje.esDevolucion && viaje.tipoDevolucion === 'parcial') return 'border border-orange-200 bg-orange-50 text-orange-700';
  if (viaje.esDevolucion) return 'border border-rose-200 bg-rose-50 text-rose-700';
  return estadoBadge(viaje);
}

function getRecordedAmount(viaje: Viaje): number | undefined {
  if (viaje.esDevolucion) return viaje.montoCobroViaje ?? 0;

  if (viaje.pagoEntrega) {
    return viaje.pagoEntrega.tipo === 'nota_credito'
      ? viaje.pagoEntrega.valorNotaCredito
      : viaje.pagoEntrega.monto;
  }

  return parseAmountInput(splitPaymentReference(viaje.referenciaPago).valor);
}

function formatDateTime(timestamp?: number): string {
  if (!timestamp) return 'Sin fecha registrada';
  return new Intl.DateTimeFormat('es-EC', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(timestamp));
}

function shouldPreferCameraCapture(): boolean {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return false;
  }

  const coarsePointer = typeof window.matchMedia === 'function'
    ? window.matchMedia('(pointer: coarse)').matches
    : false;
  const userAgentData = (navigator as Navigator & { userAgentData?: { mobile?: boolean } }).userAgentData;
  const mobileUserAgent = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile/i.test(navigator.userAgent || '');

  return Boolean(userAgentData?.mobile || mobileUserAgent || coarsePointer);
}

function sameDay(timestampA: number, timestampB: number): boolean {
  const left = new Date(timestampA);
  const right = new Date(timestampB);
  return left.getFullYear() === right.getFullYear()
    && left.getMonth() === right.getMonth()
    && left.getDate() === right.getDate();
}

function formatShortDateTime(timestamp?: number): string {
  if (!timestamp) return 'Ahora';
  return new Intl.DateTimeFormat('es-EC', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(timestamp));
}

export default function ConductorPanel() {
  const { user, logout } = useAuth();
  const [section, setSection] = useState<Section>('dashboard');
  const [conductor, setConductor] = useState<Conductor | null>(null);
  const [viajes, setViajes] = useState<Viaje[]>([]);
  const [historialViajes, setHistorialViajes] = useState<Viaje[]>([]);
  const [historialTotalRegistros, setHistorialTotalRegistros] = useState(0);
  const [historialCargando, setHistorialCargando] = useState(false);
  const [viaje, setViaje] = useState<Viaje | null>(null);
  const [paso, setPaso] = useState<Paso>(1);
  const [paquete, setPaquete] = useState<Paquete>(createEmptyPaquete());
  const [preferCameraCapture, setPreferCameraCapture] = useState(false);
  const [cameraTarget, setCameraTarget] = useState<CameraUploadTarget | null>(null);
  const [cameraError, setCameraError] = useState('');
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraStreamVersion, setCameraStreamVersion] = useState(0);
  const cameraVideoRef = useRef<HTMLVideoElement | null>(null);
  const cameraStreamRef = useRef<MediaStream | null>(null);

  const watchIdRef = useRef<number | null>(null);
  const trackingIntervalRef = useRef<number | null>(null);
  const latestTrackingCoordRef = useRef<Coordenada | null>(null);
  const lastSentTrackingTimestampRef = useRef<number | null>(null);
  const isSendingTrackingRef = useRef(false);
  const [geoError, setGeoError] = useState('');
  const [coordActual, setCoordActual] = useState<{ lat: number; lng: number } | null>(null);

  const [direccionEntrega, setDireccionEntrega] = useState('');
  const [gasolina, setGasolina] = useState<RegistroGasolina>(createEmptyGasolina());
  const [esDevolucion, setEsDevolucion] = useState(false);
  const [formaPago, setFormaPago] = useState<FormaPagoSeleccion>('');
  const [valorPago, setValorPago] = useState('');
  const [referenciaPago, setReferenciaPago] = useState('');
  const [bancoPago, setBancoPago] = useState('');
  const [kmRecorridos, setKmRecorridos] = useState(0);
  const [imagenesEntrega, setImagenesEntrega] = useState<string[]>([]);
  const [observaciones, setObservaciones] = useState('');
  const [motivoDevolucion, setMotivoDevolucion] = useState('');
  const [uploadingImg, setUploadingImg] = useState(false);
  const [uploadingPaquete, setUploadingPaquete] = useState(false);
  const [signingPdf, setSigningPdf] = useState(false);
  const [signedPdfName, setSignedPdfName] = useState('');
  const [signedPdfNames, setSignedPdfNames] = useState<Record<number, string>>({});
  const [guiasFirmadasPendientes, setGuiasFirmadasPendientes] = useState<GuiaFirmadaViaje[]>([]);
  const [guiasDevolucionSeleccionadas, setGuiasDevolucionSeleccionadas] = useState<number[]>([]);
  const [itemsDevolucionSeleccionados, setItemsDevolucionSeleccionados] = useState<DevolucionesPorGuia>({});
  const [signatureModalOpen, setSignatureModalOpen] = useState(false);
  const [signatureGuiaIndex, setSignatureGuiaIndex] = useState<number | null>(null);
  const [termsModalOpen, setTermsModalOpen] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsPreviewOpen, setTermsPreviewOpen] = useState(false);
  const [terminosUrl, setTerminosUrl] = useState('');
  const [historialDetalleViaje, setHistorialDetalleViaje] = useState<Viaje | null>(null);
  const [asignadasEstadoFiltro, setAsignadasEstadoFiltro] = useState<AsignadasEstadoFiltro>('todas');
  const [historialPagina, setHistorialPagina] = useState(1);
  const [historialBusqueda, setHistorialBusqueda] = useState('');
  const [historialTipoFiltro, setHistorialTipoFiltro] = useState<HistorialTipoFiltro>('todas');
  const [historialEstadoFiltro, setHistorialEstadoFiltro] = useState<HistorialEstadoFiltro>('todas');
  const [cargasGasolina, setCargasGasolina] = useState<CargaGasolina[]>([]);
  const [cargasGasolinaPagina, setCargasGasolinaPagina] = useState(1);
  const [cargasGasolinaTotalRegistros, setCargasGasolinaTotalRegistros] = useState(0);
  const [cargasGasolinaCargando, setCargasGasolinaCargando] = useState(false);
  const [cargaGasolinaVehiculoId, setCargaGasolinaVehiculoId] = useState('');
  const [cargaGasolinaFormaPago, setCargaGasolinaFormaPago] = useState<FormaPagoCargaGasolina>('efectivo');
  const [cargaGasolinaUltimosCuatro, setCargaGasolinaUltimosCuatro] = useState('');
  const [cargaGasolinaNumeroVoucher, setCargaGasolinaNumeroVoucher] = useState('');
  const [cargaGasolinaImgFacturavou, setCargaGasolinaImgFacturavou] = useState('');
  const [cargaGasolinaCantidadGalones, setCargaGasolinaCantidadGalones] = useState('');
  const [cargaGasolinaTotalPagado, setCargaGasolinaTotalPagado] = useState('');
  const [cargaGasolinaSaving, setCargaGasolinaSaving] = useState(false);
  const [uploadingCargaGasolinaImg, setUploadingCargaGasolinaImg] = useState(false);
  const [cargaGasolinaError, setCargaGasolinaError] = useState('');
  const [cargaGasolinaMensaje, setCargaGasolinaMensaje] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [syncError, setSyncError] = useState('');
  const [notificationPermission, setNotificationPermission] = useState<BrowserNotificationPermission>(getInitialNotificationPermission);
  const [notificationError, setNotificationError] = useState('');
  const [requestingNotificationPermission, setRequestingNotificationPermission] = useState(false);
  const [iniciandoViaje, setIniciandoViaje] = useState(false);
  const [preparandoEntrega, setPreparandoEntrega] = useState(false);
  const [dashboardStats, setDashboardStats] = useState<DashboardStatsData>(EMPTY_DASHBOARD_STATS);
  const observacionesAutoDevolucionRef = useRef('');
  const observacionesRef = useRef(observaciones);
  observacionesRef.current = observaciones;
  const valorPagoAutoRef = useRef('');
  const selectedViajeIdRef = useRef<string | null>(null);
  const assignmentNotificationBaselineRef = useRef<Set<string> | null>(null);
  const notifiedAssignmentIdsRef = useRef<Set<string>>(new Set<string>());

  const isMobileNotificationDevice = useMemo(() => isLikelyMobileNotificationDevice(), []);

  const coordenadaMapaActual = useMemo<Coordenada | undefined>(() => {
    if (coordActual) {
      return {
        lat: coordActual.lat,
        lng: coordActual.lng,
        timestamp: latestTrackingCoordRef.current?.timestamp ?? viaje?.coordenadaActual?.timestamp ?? Date.now(),
      };
    }

    return viaje?.coordenadaActual;
  }, [coordActual, viaje?.coordenadaActual]);

  const applySelectedViaje = useCallback((nextViaje: Viaje | null, nextStep?: Paso) => {
    const sameSelectedViaje = Boolean(nextViaje?.id && nextViaje.id === selectedViajeIdRef.current);
    const remoteSignedGuides = getGuiasFirmadasViaje(nextViaje);
    const remoteReturnedGuideIndices = getReturnedGuideIndices(nextViaje);
    const remoteReturnedGuideItems = getReturnedGuideItemSelections(nextViaje);
    const structuredPayment = nextViaje?.pagoEntrega;
    const paymentReference = splitPaymentReference(nextViaje?.referenciaPago);
    const paymentAmount = structuredPayment?.tipo === 'nota_credito'
      ? structuredPayment.valorNotaCredito
      : structuredPayment?.monto;
    const paymentCode = structuredPayment?.numeroTransferencia
      ?? structuredPayment?.numeroCheque
      ?? structuredPayment?.numeroNotaCredito
      ?? paymentReference.referencia;

    setViaje(nextViaje);
    selectedViajeIdRef.current = nextViaje?.id ?? null;
    if (!sameSelectedViaje) {
      setPaquete(nextViaje?.paquete ?? createEmptyPaquete());
      setDireccionEntrega(nextViaje?.direccionEntrega || nextViaje?.paquete?.cliente?.direccion || '');
      setEsDevolucion(Boolean(nextViaje?.esDevolucion));
      setFormaPago(nextViaje?.esDevolucion || nextViaje?.tipoEntrega === 'transferencia' ? '' : structuredPayment?.tipo ?? nextViaje?.formaPago ?? '');
      setValorPago(
        nextViaje?.esDevolucion || nextViaje?.tipoEntrega === 'transferencia'
          ? ''
          : typeof paymentAmount === 'number'
          ? formatAmountInput(paymentAmount)
          : paymentReference.valor,
      );
      setReferenciaPago(nextViaje?.esDevolucion || nextViaje?.tipoEntrega === 'transferencia' ? '' : paymentCode);
      setBancoPago(nextViaje?.esDevolucion || nextViaje?.tipoEntrega === 'transferencia' ? '' : structuredPayment?.banco ?? '');
      setKmRecorridos(nextViaje?.kilometrosRecorridos ?? 0);
      setImagenesEntrega(
        nextViaje?.imagenesEntrega?.length
          ? nextViaje.imagenesEntrega
          : nextViaje?.imagenEntrega
          ? [nextViaje.imagenEntrega]
          : [],
      );
      setObservaciones(nextViaje?.observaciones ?? '');
      setMotivoDevolucion('');
    }
    setGuiasFirmadasPendientes(current => {
      if (!nextViaje) return [];
      if (!sameSelectedViaje) return remoteSignedGuides;
      return remoteSignedGuides.length > current.length ? remoteSignedGuides : current;
    });
    setGuiasDevolucionSeleccionadas(current => {
      if (!nextViaje) return [];
      if (!sameSelectedViaje) return remoteReturnedGuideIndices;
      return remoteReturnedGuideIndices.length > 0 ? remoteReturnedGuideIndices : current;
    });
    setItemsDevolucionSeleccionados(current => {
      if (!nextViaje) return {};
      if (!sameSelectedViaje) return remoteReturnedGuideItems;
      return Object.keys(remoteReturnedGuideItems).length > 0 ? remoteReturnedGuideItems : current;
    });
    if (!sameSelectedViaje) {
      setSignedPdfName('');
      setSignedPdfNames({});
      setSignatureModalOpen(false);
      setSignatureGuiaIndex(null);
      setTermsModalOpen(false);
      setTermsAccepted(false);
      setTermsPreviewOpen(false);
      observacionesAutoDevolucionRef.current = '';
      valorPagoAutoRef.current = '';
      setGasolina(
        nextViaje?.gasolina
          ? {
              tipo: nextViaje.gasolina.tipo,
              galones: nextViaje.gasolina.galones,
              precioPorGalon: nextViaje.gasolina.precioPorGalon,
            }
          : createEmptyGasolina(),
      );
    }
    setCoordActual(current => {
      if (watchIdRef.current !== null && latestTrackingCoordRef.current) {
        return {
          lat: latestTrackingCoordRef.current.lat,
          lng: latestTrackingCoordRef.current.lng,
        };
      }

      return nextViaje?.coordenadaActual
        ? { lat: nextViaje.coordenadaActual.lat, lng: nextViaje.coordenadaActual.lng }
        : null;
    });
    setPaso(currentStep => {
      if (nextStep) return nextStep;
      return getPasoViaje(nextViaje, currentStep);
    });
  }, []);

  const refreshRemoteState = useCallback(async () => {
    if (!user) return;

    try {
      const nextConductor = await getConductorByUserId(user.id);
      setConductor(nextConductor ?? null);

      if (!nextConductor) {
        setViajes([]);
        setHistorialViajes([]);
        setHistorialTotalRegistros(0);
        setDashboardStats(EMPTY_DASHBOARD_STATS);
        applySelectedViaje(null, 1);
        setSyncError('');
        return;
      }

      const [nextViajesPendientes, nextViajesEnCurso, nextDashboardStats] = await Promise.all([
        getViajesAsignadosPendientesConductor(),
        getViajesAsignadosEnCursoConductor(),
        getConductorDashboardStats(),
      ]);
      const nextViajes = [...nextViajesPendientes, ...nextViajesEnCurso].sort(sortViajes);
      setViajes(nextViajes);
      setDashboardStats(nextDashboardStats);

      const selected = nextViajes.find(candidate => candidate.id === selectedViajeIdRef.current && candidate.estado !== 'finalizado') ?? null;
      applySelectedViaje(selected);
      setSyncError('');
    } catch (err: unknown) {
      setSyncError(
        (err as { message?: string })?.message ??
          'No se pudo sincronizar tu panel de conductor con el servidor.',
      );
    }
  }, [applySelectedViaje, user]);

  // ── Dashboard: carga única al entrar, incluye estadísticas ─────────────────

  const refreshDashboard = useCallback(async () => {
    if (!user) return;
    try {
      const nextConductor = await getConductorByUserId(user.id);
      setConductor(nextConductor ?? null);
      if (!nextConductor) {
        setViajes([]);
        setDashboardStats(EMPTY_DASHBOARD_STATS);
        setSyncError('');
        return;
      }
      const [nextViajesPendientes, nextViajesEnCurso, nextDashboardStats] = await Promise.all([
        getViajesAsignadosPendientesConductor(),
        getViajesAsignadosEnCursoConductor(),
        getConductorDashboardStats(),
      ]);
      setViajes([...nextViajesPendientes, ...nextViajesEnCurso].sort(sortViajes));
      setDashboardStats(nextDashboardStats);
      setSyncError('');
    } catch (err: unknown) {
      setSyncError((err as { message?: string })?.message ?? 'No se pudo cargar el dashboard.');
    }
  }, [user]);

  // ── Entregas Asignadas: carga única al entrar, sin dashboard stats ─────────

  const refreshAsignadas = useCallback(async () => {
    if (!user) return;
    try {
      const [nextViajesPendientes, nextViajesEnCurso] = await Promise.all([
        getViajesAsignadosPendientesConductor(),
        getViajesAsignadosEnCursoConductor(),
      ]);
      const nextViajes = [...nextViajesPendientes, ...nextViajesEnCurso].sort(sortViajes);
      setViajes(nextViajes);
      setSyncError('');
    } catch (err: unknown) {
      setSyncError((err as { message?: string })?.message ?? 'No se pudo cargar las entregas asignadas.');
    }
  }, [user]);

  const refreshTrackingState = useCallback(async () => {
    if (!user) return;

    try {
      const [nextViajesPendientes, nextViajesEnCurso] = await Promise.all([
        getViajesAsignadosPendientesConductor(),
        getViajesAsignadosEnCursoConductor(),
      ]);
      const nextViajes = [...nextViajesPendientes, ...nextViajesEnCurso].sort(sortViajes);
      setViajes(nextViajes);

      const selected = nextViajes.find(candidate => candidate.id === selectedViajeIdRef.current && candidate.estado !== 'finalizado') ?? null;
      if (!selected) {
        setSyncError('');
        return;
      }

      setViaje(current => {
        if (!current || current.id !== selected.id) return current;

        return {
          ...current,
          estado: selected.estado,
          coordenadas: selected.coordenadas,
          coordenadaActual: selected.coordenadaActual,
          coordenadaInicio: selected.coordenadaInicio,
          coordenadaFin: selected.coordenadaFin,
          puntosIntermedios: selected.puntosIntermedios,
          fechaInicio: selected.fechaInicio,
          fechaFin: selected.fechaFin,
        };
      });
      setPaso(currentStep => getPasoViaje(selected, currentStep));
      setSyncError('');
    } catch (err: unknown) {
      setSyncError(
        (err as { message?: string })?.message ??
          'No se pudo sincronizar el mapa del viaje actual con el servidor.',
      );
    }
  }, [user]);

  const refreshHistorial = useCallback(async (
    page = historialPagina,
    search = historialBusqueda,
    tipoEntrega = historialTipoFiltro,
    estado = historialEstadoFiltro,
  ) => {
    try {
      setHistorialCargando(true);
      const result = await getViajesPaginados({
        estado: 'completado',
        page,
        page_size: HISTORIAL_PAGE_SIZE,
        search: search.trim() || undefined,
        tipo_entrega: tipoEntrega !== 'todas' ? tipoEntrega : undefined,
        es_devolucion: estado === 'todas' ? undefined : estado === 'devolucion',
      });
      setHistorialViajes(result.items);
      setHistorialTotalRegistros(result.count);
      setSyncError('');
    } catch (err: unknown) {
      setSyncError(
        (err as { message?: string })?.message ??
          'No se pudo sincronizar el historial del conductor con el servidor.',
      );
    } finally {
      setHistorialCargando(false);
    }
  }, [historialBusqueda, historialEstadoFiltro, historialPagina, historialTipoFiltro]);

  useEffect(() => {
    if (!user) {
      setConductor(null);
      setViajes([]);
      setHistorialViajes([]);
      setHistorialTotalRegistros(0);
      applySelectedViaje(null, 1);
      return;
    }

    void refreshRemoteState();
  }, [applySelectedViaje, refreshRemoteState, user]);

  useEffect(() => {
    let cancelled = false;

    async function loadTerminos() {
      try {
        const response = await listarParametrosPublicos();
        if (cancelled) return;

        const parametro = response.data.find(item => item.slug === 'terminos_y_condiciones_url');
        setTerminosUrl((parametro?.valor || '').trim());
      } catch {
        if (!cancelled) {
          setTerminosUrl('');
        }
      }
    }

    void loadTerminos();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!canUseAssignmentNotifications()) {
      setNotificationPermission('unsupported');
      return;
    }

    const syncPermission = () => {
      setNotificationPermission(Notification.permission);
    };

    syncPermission();
    window.addEventListener('focus', syncPermission);

    return () => {
      window.removeEventListener('focus', syncPermission);
    };
  }, []);

  const viajesActivos = useMemo(() => viajes.filter(item => item.estado !== 'finalizado'), [viajes]);
  const viajesPendientes = useMemo(() => viajes.filter(item => item.estado === 'pendiente'), [viajes]);
  const totalFactura = useMemo(() => getInvoiceTotal(viaje?.paquete), [viaje?.paquete]);
  const saldoPendiente = useMemo(() => getPendingAmount(viaje?.paquete), [viaje?.paquete]);
  const requiereEvidenciaEntrega = viaje?.tipoEntrega === 'entrega';
  const isEnvio = viaje?.tipoEntrega === 'envio';
  const isTransferencia = viaje?.tipoEntrega === 'transferencia';
  const permiteDevolucion = viaje?.tipoEntrega === 'entrega';
  const devolucionActiva = permiteDevolucion && esDevolucion;
  const firmaGuiaObligatoria = !isEnvio && !isTransferencia;
  const guiasPdfViaje = useMemo(() => getGuiasPdfViaje(viaje), [viaje]);
  const remoteGuiasFirmadasViaje = useMemo(() => getGuiasFirmadasViaje(viaje), [viaje]);
  const guiasFirmadasViaje = remoteGuiasFirmadasViaje.length > 0 ? remoteGuiasFirmadasViaje : guiasFirmadasPendientes;
  const hasMultipleGuias = guiasPdfViaje.length > 1;
  const guiasDevueltasSeleccionadas = useMemo(
    () => guiasDevolucionSeleccionadas.map(index => guiasPdfViaje[index]).filter((guia): guia is GuiaPdfViaje => Boolean(guia)),
    [guiasDevolucionSeleccionadas, guiasPdfViaje],
  );
  const guiasDevueltasIndexSet = useMemo(() => new Set(guiasDevolucionSeleccionadas), [guiasDevolucionSeleccionadas]);
  const firmaOverlayActiva = signatureModalOpen || termsModalOpen || termsPreviewOpen;
  const cantidadProductosDevueltos = useMemo(
    () => Object.values(itemsDevolucionSeleccionados).reduce((total, itemKeys) => total + itemKeys.length, 0),
    [itemsDevolucionSeleccionados],
  );
  const haySeleccionDevolucion = guiasDevolucionSeleccionadas.length > 0 || cantidadProductosDevueltos > 0;
  const valorGuiasDevueltas = useMemo(
    () => Number(
      guiasPdfViaje
        .reduce(
          (total, guia, index) => total + getGuideReturnedAmount(guia, index, guiasDevueltasIndexSet, itemsDevolucionSeleccionados),
          0,
        )
        .toFixed(2),
    ),
    [guiasDevueltasIndexSet, guiasPdfViaje, itemsDevolucionSeleccionados],
  );
  const totalFacturaAjustado = useMemo(() => {
    if (!devolucionActiva || typeof totalFactura !== 'number') return totalFactura;
    return Number(Math.max(totalFactura - valorGuiasDevueltas, 0).toFixed(2));
  }, [devolucionActiva, totalFactura, valorGuiasDevueltas]);
  const tipoDevolucionSeleccionada = useMemo(() => {
    if (!devolucionActiva) return '';
    if (!haySeleccionDevolucion) return 'total';
    if (typeof totalFacturaAjustado === 'number' && totalFacturaAjustado > 0) {
      return 'parcial';
    }
    return 'total';
  }, [devolucionActiva, haySeleccionDevolucion, totalFacturaAjustado]);
  const montoCobroGuiasFirmadas = useMemo(
    () => Number(
      guiasPdfViaje
        .reduce((total, guia, index) => {
          if (!guiasFirmadasViaje[index]?.url) return total;
          const guideTotal = getGuiaTotal(guia);
          const returnedAmount = getGuideReturnedAmount(guia, index, guiasDevueltasIndexSet, itemsDevolucionSeleccionados);
          return total + Math.max(guideTotal - returnedAmount, 0);
        }, 0)
        .toFixed(2),
    ),
    [guiasDevueltasIndexSet, guiasFirmadasViaje, guiasPdfViaje, itemsDevolucionSeleccionados],
  );
  const montoCierre = useMemo(() => {
    if (viaje?.tipoEntrega === 'envio') {
      return viaje.valorTotalEnvio ?? 0;
    }
    return getConfiguredPaymentAmount(viaje?.paquete);
  }, [viaje]);
  const montoCierreAjustado = useMemo(() => {
    if (!devolucionActiva || typeof montoCierre !== 'number') return montoCierre;
    return Number(Math.max(montoCierre - valorGuiasDevueltas, 0).toFixed(2));
  }, [devolucionActiva, montoCierre, valorGuiasDevueltas]);
  const pagoPactadoHint = useMemo(() => getAssignedPaymentHint(viaje ?? undefined), [viaje]);
  const devolucionPermiteCobro = devolucionActiva && tipoDevolucionSeleccionada === 'parcial' && !isTransferencia;
  const pagoCompletoBloqueado = !isEnvio && !isTransferencia && !devolucionPermiteCobro && viaje?.paquete?.pago?.tipo === 'completo';
  const externalMapUrl = useMemo(() => getExternalMapUrl(viaje), [viaje]);
  const detalleDevolucionSugerido = useMemo(
    () => (devolucionActiva ? buildReturnObservations(guiasPdfViaje, guiasDevueltasIndexSet, itemsDevolucionSeleccionados) : ''),
    [devolucionActiva, guiasDevueltasIndexSet, guiasPdfViaje, itemsDevolucionSeleccionados],
  );
  const observacionesDevolucionSugeridas = useMemo(
    () => (devolucionActiva ? buildAutoReturnObservations(motivoDevolucion, detalleDevolucionSugerido) : ''),
    [detalleDevolucionSugerido, devolucionActiva, motivoDevolucion],
  );
  const shouldShowNotificationBanner = isMobileNotificationDevice && notificationPermission !== 'granted';
  const resumenPagoHint = devolucionActiva
    ? haySeleccionDevolucion
      ? devolucionPermiteCobro
        ? `Esta entrega se guardará como devolución parcial. Se descontarán ${formatCurrency(valorGuiasDevueltas)} y podrás registrar el cobro restante de ${formatCurrency(montoCierreAjustado)}.`
        : `Esta entrega se guardará como devolución total. Se descontarán ${formatCurrency(valorGuiasDevueltas)} y no se registrará forma de pago.`
      : 'Selecciona al menos una guía completa o uno o más productos devueltos para cerrar como devolución.'
    : isTransferencia
    ? 'Esta transferencia se cerrará sin forma de pago ni comprobante. La firma de la guía es opcional.'
    : pagoPactadoHint;

  useEffect(() => {
    if (!viaje || viaje.estado === 'finalizado') return;

    if (devolucionActiva && !devolucionPermiteCobro) {
      if (direccionEntrega) setDireccionEntrega('');
      if (formaPago) setFormaPago('');
      if (valorPago) setValorPago('');
      if (referenciaPago) setReferenciaPago('');
      if (bancoPago) setBancoPago('');
      valorPagoAutoRef.current = '';
      return;
    }

    if (devolucionActiva && direccionEntrega) {
      setDireccionEntrega('');
    }

    if (isTransferencia) {
      if (formaPago) setFormaPago('');
      if (valorPago) setValorPago('');
      if (referenciaPago) setReferenciaPago('');
      if (bancoPago) setBancoPago('');
      if (!direccionEntrega) {
        setDireccionEntrega(viaje.direccionEntrega || viaje.paquete?.cliente?.direccion || '');
      }
      return;
    }

    if (!direccionEntrega) {
      setDireccionEntrega(viaje.direccionEntrega || viaje.paquete?.cliente?.direccion || '');
    }

    const pagoPactado = viaje.paquete?.pago;
    const montoObjetivo = devolucionActiva ? montoCierreAjustado : montoCierre;
    const nextAutoPayment = typeof montoObjetivo === 'number' ? formatAmountInput(montoObjetivo) : '';
    if (nextAutoPayment && (!valorPago || valorPago === valorPagoAutoRef.current)) {
      setValorPago(nextAutoPayment);
    }
    valorPagoAutoRef.current = nextAutoPayment;

    if (!isEnvio && !isTransferencia && !devolucionPermiteCobro && pagoPactado?.tipo === 'completo') {
      const nextAmount = formatAmountInput(montoCierre);
      if (formaPago !== 'completo') setFormaPago('completo');
      if (valorPago !== nextAmount) setValorPago(nextAmount);
      valorPagoAutoRef.current = nextAmount;
      if (referenciaPago) setReferenciaPago('');
      if (bancoPago) setBancoPago('');
      return;
    }

    // Envío: por defecto Efectivo; el conductor puede elegir otra forma de pago.
    if (isEnvio) {
      if (!formaPago) setFormaPago('efectivo');
      return;
    }

    // En pago parcial se preselecciona el medio acordado para el saldo.
    const medioPactado = pagoPactado?.tipo === 'parcial' ? pagoPactado.tipoPagoParcial : pagoPactado?.tipo;
    if (!formaPago && medioPactado && FORMAS_PAGO_ENTREGA.includes(medioPactado as (typeof FORMAS_PAGO_ENTREGA)[number])) {
      setFormaPago(medioPactado as FormaPagoSeleccion);
    }
  }, [
    bancoPago,
    devolucionPermiteCobro,
    devolucionActiva,
    direccionEntrega,
    formaPago,
    isEnvio,
    isTransferencia,
    montoCierre,
    montoCierreAjustado,
    observacionesDevolucionSugeridas,
    referenciaPago,
    valorPago,
    viaje,
  ]);

  useEffect(() => {
    const currentObservaciones = observacionesRef.current;
    const previousAuto = observacionesAutoDevolucionRef.current;

    if (!devolucionActiva) {
      if (previousAuto) {
        const nextObservaciones = stripAutoReturnObservations(currentObservaciones, previousAuto, viaje?.observaciones ?? '');
        if (nextObservaciones !== currentObservaciones) {
          setObservaciones(nextObservaciones);
        }
      }
      observacionesAutoDevolucionRef.current = '';
      return;
    }

    if (!observacionesDevolucionSugeridas) {
      if (previousAuto) {
        const nextObservaciones = stripAutoReturnObservations(currentObservaciones, previousAuto);
        if (nextObservaciones !== currentObservaciones) {
          setObservaciones(nextObservaciones);
        }
      }
      observacionesAutoDevolucionRef.current = '';
      return;
    }

    const nextObservaciones = mergeAutoReturnObservations(currentObservaciones, observacionesDevolucionSugeridas, previousAuto);
    if (nextObservaciones !== currentObservaciones) {
      setObservaciones(nextObservaciones);
    }
    observacionesAutoDevolucionRef.current = observacionesDevolucionSugeridas;
  }, [devolucionActiva, observacionesDevolucionSugeridas, viaje?.observaciones]);

  function handleToggleDevolucion(checked: boolean) {
    if (!permiteDevolucion) {
      setEsDevolucion(false);
      setMotivoDevolucion('');
      setGuiasDevolucionSeleccionadas([]);
      setItemsDevolucionSeleccionados({});
      return;
    }

    setEsDevolucion(checked);
    if (!checked) {
      setMotivoDevolucion('');
      setGuiasDevolucionSeleccionadas([]);
      setItemsDevolucionSeleccionados({});
      return;
    }

    setMotivoDevolucion('');
    const persistedSelection = getReturnedGuideIndices(viaje);
    setGuiasDevolucionSeleccionadas(persistedSelection);
    setItemsDevolucionSeleccionados(getReturnedGuideItemSelections(viaje));
  }

  async function handleEnableAssignmentNotifications() {
    if (!canUseAssignmentNotifications()) {
      setNotificationPermission('unsupported');
      setNotificationError('Este dispositivo o navegador no permite notificaciones web en esta sesión.');
      return;
    }

    setRequestingNotificationPermission(true);
    setNotificationError('');
    try {
      if ('serviceWorker' in navigator) {
        await navigator.serviceWorker.ready;
      }

      const permission = await Notification.requestPermission();
      setNotificationPermission(permission);

      if (permission === 'denied') {
        setNotificationError('Las notificaciones quedaron bloqueadas. Debes habilitarlas en la configuración del navegador del dispositivo.');
        return;
      }
      if (permission !== 'granted') {
        setNotificationError('No se concedió el permiso para notificaciones.');
        return;
      }
    } catch (err: unknown) {
      setNotificationError((err as { message?: string })?.message ?? 'No se pudo solicitar el permiso de notificaciones.');
    } finally {
      setRequestingNotificationPermission(false);
    }
  }

  function toggleGuiaDevolucion(index: number) {
    setGuiasDevolucionSeleccionadas(current => {
      if (current.includes(index)) {
        return current.filter(currentIndex => currentIndex !== index);
      }

      return [...current, index].sort((left, right) => left - right);
    });
    setItemsDevolucionSeleccionados(current => {
      if (!current[index]?.length) return current;
      const next = { ...current };
      delete next[index];
      return next;
    });
  }

  function toggleGuiaItemDevolucion(index: number, itemKey: string) {
    setGuiasDevolucionSeleccionadas(current => current.filter(currentIndex => currentIndex !== index));
    setItemsDevolucionSeleccionados(current => {
      const currentItems = current[index] ?? [];
      const nextItems = currentItems.includes(itemKey)
        ? currentItems.filter(currentKey => currentKey !== itemKey)
        : [...currentItems, itemKey];
      const next = { ...current };
      if (nextItems.length === 0) {
        delete next[index];
      } else {
        next[index] = nextItems;
      }
      return next;
    });
  }

  useEffect(() => {
    if (!user) {
      return;
    }

    // Tracking en tiempo real solo para Viaje Actual paso 2 (GPS)
    const shouldSyncTracking = section === 'actual'
      && paso === 2
      && viaje?.estado === 'en_curso'
      && viaje?.tipoEntrega !== 'transferencia';

    if (!shouldSyncTracking) {
      return;
    }

    const interval = window.setInterval(() => {
      void refreshTrackingState();
    }, CONDUCTOR_ACTIVE_TRIP_SYNC_INTERVAL_MS);

    return () => {
      window.clearInterval(interval);
    };
  }, [user, paso, refreshTrackingState, section, viaje?.estado, viaje?.tipoEntrega]);

  // ── Carga única al entrar a cada sección (sin polling) ─────────────────────

  useEffect(() => {
    if (!user) return;
    if (section === 'dashboard') {
      void refreshDashboard();
    } else if (section === 'asignadas') {
      void refreshAsignadas();
    }
  }, [section, user, refreshDashboard, refreshAsignadas]);

  useEffect(() => {
    const conductorId = conductor?.id;
    if (!conductorId) {
      assignmentNotificationBaselineRef.current = null;
      notifiedAssignmentIdsRef.current = new Set<string>();
      return;
    }

    const storageKey = getAssignmentNotificationStorageKey(conductorId);
    const assignedTrips = getAssignedTripsForConductor(viajes, conductorId);
    const currentIds = new Set(assignedTrips.map(viajeItem => buildAssignedTripNotificationId(viajeItem, conductorId)));

    if (assignmentNotificationBaselineRef.current === null) {
      const storedIds = readStoredAssignmentNotificationIds(storageKey);
      notifiedAssignmentIdsRef.current = new Set([...storedIds, ...currentIds]);
      assignmentNotificationBaselineRef.current = currentIds;
      persistAssignmentNotificationIds(storageKey, notifiedAssignmentIdsRef.current);
      return;
    }

    const previousIds = assignmentNotificationBaselineRef.current;
    const newTrips = assignedTrips.filter(viajeItem => {
      const notificationId = buildAssignedTripNotificationId(viajeItem, conductorId);
      return !previousIds.has(notificationId) && !notifiedAssignmentIdsRef.current.has(notificationId);
    });

    assignmentNotificationBaselineRef.current = currentIds;

    if (newTrips.length === 0) {
      return;
    }

    newTrips.forEach(viajeItem => {
      notifiedAssignmentIdsRef.current.add(buildAssignedTripNotificationId(viajeItem, conductorId));
    });
    persistAssignmentNotificationIds(storageKey, notifiedAssignmentIdsRef.current);

    if (notificationPermission !== 'granted') {
      return;
    }

    void Promise.all(newTrips.map(viajeItem => showAssignedTripNotification(viajeItem)));
  }, [conductor?.id, notificationPermission, viajes]);

  useEffect(() => {
    if (section !== 'asignadas') return;
    setAsignadasEstadoFiltro('todas');
  }, [section]);

  useEffect(() => {
    if (section !== 'historial') return;
    setHistorialPagina(1);
  }, [section]);

  useEffect(() => {
    if (section !== 'gasolina') return;
    setCargasGasolinaPagina(1);
  }, [section]);

  useEffect(() => {
    setHistorialPagina(1);
  }, [historialBusqueda, historialEstadoFiltro, historialTipoFiltro]);

  useEffect(() => {
    if (section !== 'historial') return;
    void refreshHistorial();
  }, [historialBusqueda, historialEstadoFiltro, historialPagina, historialTipoFiltro, refreshHistorial, section]);

  const conductorVehiculos = useMemo(() => {
    if (!conductor) return [];
    if (conductor.vehiculos?.length) return conductor.vehiculos;
    return conductor.vehiculo ? [conductor.vehiculo] : [];
  }, [conductor]);

  useEffect(() => {
    if (conductorVehiculos.length === 0) {
      setCargaGasolinaVehiculoId('');
      return;
    }

    const seleccionExiste = conductorVehiculos.some(vehiculo => vehiculo.id === cargaGasolinaVehiculoId);
    if (!seleccionExiste) {
      const [firstVehiculo] = conductorVehiculos;
      if (firstVehiculo) {
        setCargaGasolinaVehiculoId(firstVehiculo.id);
      }
    }
  }, [cargaGasolinaVehiculoId, conductorVehiculos]);

  const navItems: PanelNavItem[] = [
    { key: 'dashboard', label: SECTION_COPY.dashboard.label, icon: SECTION_COPY.dashboard.icon },
    { key: 'actual', label: SECTION_COPY.actual.label, icon: SECTION_COPY.actual.icon, count: viajesActivos.length || undefined },
    { key: 'asignadas', label: SECTION_COPY.asignadas.label, icon: SECTION_COPY.asignadas.icon, count: viajesPendientes.length || undefined },
    { key: 'gasolina', label: SECTION_COPY.gasolina.label, icon: SECTION_COPY.gasolina.icon, count: cargasGasolinaTotalRegistros || undefined },
    { key: 'historial', label: SECTION_COPY.historial.label, icon: SECTION_COPY.historial.icon, count: historialTotalRegistros || undefined },
  ];

  const viajesEnCurso = useMemo(() => viajesActivos.filter(item => item.estado === 'en_curso'), [viajesActivos]);
  const viajeEnCursoActual = useMemo(() => viajesEnCurso[0] ?? null, [viajesEnCurso]);
  const viajesAsignadosFiltrados = useMemo(() => {
    if (asignadasEstadoFiltro === 'todas') return viajesActivos;
    return viajesActivos.filter(item => item.estado === asignadasEstadoFiltro);
  }, [asignadasEstadoFiltro, viajesActivos]);
  const historialTotalPaginas = useMemo(
    () => Math.max(1, Math.ceil(historialTotalRegistros / HISTORIAL_PAGE_SIZE)),
    [historialTotalRegistros],
  );
  const historialPaginaActual = Math.min(historialPagina, historialTotalPaginas);

  const cargasGasolinaTotalPaginas = useMemo(
    () => Math.max(1, Math.ceil(cargasGasolinaTotalRegistros / HISTORIAL_PAGE_SIZE)),
    [cargasGasolinaTotalRegistros],
  );
  const cargasGasolinaPaginaActual = Math.min(cargasGasolinaPagina, cargasGasolinaTotalPaginas);
  const cameraTargetTitle = cameraTarget === 'paquete'
    ? 'Foto del paquete'
    : cameraTarget === 'entrega'
      ? 'Foto de entrega'
      : 'Foto del voucher o factura';
  const cameraTargetUploading = cameraTarget === 'paquete'
    ? uploadingPaquete
    : cameraTarget === 'entrega'
      ? uploadingImg
      : uploadingCargaGasolinaImg;

  useEffect(() => {
    setCargasGasolinaPagina(current => Math.min(current, cargasGasolinaTotalPaginas));
  }, [cargasGasolinaTotalPaginas]);

  function handleAbrirEntregaAsignada(item: Viaje) {
    if (item.estado === 'pendiente' && viajeEnCursoActual && viajeEnCursoActual.id !== item.id) {
      applySelectedViaje(viajeEnCursoActual, getPasoViaje(viajeEnCursoActual));
      setSection('actual');
      alert('Primero finaliza la entrega que tienes en tu viaje actual para poder empezar otra entrega.');
      return;
    }

    applySelectedViaje(item, getPasoViaje(item));
    setSection('actual');
  }

  const refreshCargasGasolina = useCallback(async (page = cargasGasolinaPagina) => {
    try {
      setCargasGasolinaCargando(true);
      const result = await getCargasGasolina({
        page,
        page_size: HISTORIAL_PAGE_SIZE,
      });
      setCargasGasolina(result.items);
      setCargasGasolinaTotalRegistros(result.count);
      setCargaGasolinaError('');
    } catch (err: unknown) {
      setCargaGasolinaError((err as { message?: string })?.message ?? 'No se pudo cargar el historial de gasolina.');
    } finally {
      setCargasGasolinaCargando(false);
    }
  }, [cargasGasolinaPagina]);

  useEffect(() => {
    if (section !== 'gasolina') return;
    void refreshCargasGasolina(cargasGasolinaPagina);
  }, [cargasGasolinaPagina, refreshCargasGasolina, section]);

  useEffect(() => {
    setPreferCameraCapture(shouldPreferCameraCapture());
  }, []);

  const stopCameraStream = useCallback(() => {
    cameraStreamRef.current?.getTracks().forEach(track => track.stop());
    cameraStreamRef.current = null;
    if (cameraVideoRef.current) {
      cameraVideoRef.current.srcObject = null;
    }
    setCameraReady(false);
  }, []);

  const closeCameraCapture = useCallback(() => {
    stopCameraStream();
    setCameraTarget(null);
    setCameraError('');
  }, [stopCameraStream]);

  useEffect(() => {
    return () => {
      stopCameraStream();
    };
  }, [stopCameraStream]);

  const attachCameraStream = useCallback(() => {
    if (!cameraTarget) return;

    const video = cameraVideoRef.current;
    const stream = cameraStreamRef.current;
    if (!video || !stream) return;

    if (video.srcObject !== stream) {
      video.srcObject = stream;
    }
    void video.play().catch(() => undefined);
  }, [cameraTarget]);

  useEffect(() => {
    attachCameraStream();
  }, [attachCameraStream, cameraStreamVersion]);

  useEffect(() => {
    if (!preferCameraCapture || !cameraTarget) return;
    if (cameraTarget === 'gasolina') {
      if (section !== 'gasolina') {
        closeCameraCapture();
      }
      return;
    }
    if (section !== 'actual' || (cameraTarget === 'paquete' && paso !== 1) || (cameraTarget === 'entrega' && paso !== 3)) {
      closeCameraCapture();
    }
  }, [cameraTarget, closeCameraCapture, paso, preferCameraCapture, section]);

  useEffect(() => {
    setHistorialPagina(current => Math.min(current, historialTotalPaginas));
  }, [historialTotalPaginas]);

  async function handleImagenesPaquete(event: ChangeEvent<HTMLInputElement>) {
    const files = Array.from(event.target.files ?? []);
    if (files.length === 0) return;

    setUploadingPaquete(true);
    try {
      const urls = await Promise.all(files.map(file => uploadFileToMinio(file, 'paquetes')));
      setPaquete(prev => ({ ...prev, imagenes: [...prev.imagenes, ...urls] }));
      setError('');
    } catch (err: unknown) {
      setError((err as { message?: string })?.message ?? 'No se pudieron subir las imagenes del paquete.');
    } finally {
      setUploadingPaquete(false);
      event.target.value = '';
    }
  }

  async function openCameraCapture(target: CameraUploadTarget) {
    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraError('La cámara no está disponible en este dispositivo o navegador.');
      return;
    }

    stopCameraStream();
    setCameraError('');
    setCameraTarget(target);
    if (target === 'gasolina') {
      setCargaGasolinaError('');
      setCargaGasolinaMensaje('');
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: 'environment' },
        },
        audio: false,
      });
      cameraStreamRef.current = stream;
      setCameraReady(false);
      setCameraStreamVersion(current => current + 1);
      if (target === 'gasolina') {
        setCargaGasolinaError('');
      } else {
        setError('');
      }
    } catch (err: unknown) {
      setCameraTarget(null);
      setCameraReady(false);
      setCameraError((err as { message?: string })?.message ?? 'No se pudo abrir la cámara del dispositivo.');
    }
  }

  async function uploadCapturedPhoto(file: File, target: CameraUploadTarget) {
    if (target === 'paquete') {
      setUploadingPaquete(true);
    } else if (target === 'entrega') {
      setUploadingImg(true);
    } else {
      setUploadingCargaGasolinaImg(true);
    }

    try {
      const uploadPrefix = target === 'paquete' ? 'paquetes' : target === 'entrega' ? 'entregas' : 'cargas-gasolina';
      const url = await uploadFileToMinio(file, uploadPrefix);
      if (target === 'paquete') {
        setPaquete(prev => ({ ...prev, imagenes: [...prev.imagenes, url] }));
        setError('');
      } else if (target === 'entrega') {
        setImagenesEntrega(current => [...current, url]);
        setError('');
      } else {
        setCargaGasolinaImgFacturavou(url);
        setCargaGasolinaError('');
        setCargaGasolinaMensaje('');
      }
      closeCameraCapture();
    } catch (err: unknown) {
      const message = (err as { message?: string })?.message ?? 'No se pudo subir la foto capturada.';
      if (target === 'gasolina') {
        setCargaGasolinaMensaje('');
        setCargaGasolinaError(message);
      } else {
        setError(message);
      }
    } finally {
      if (target === 'paquete') {
        setUploadingPaquete(false);
      } else if (target === 'entrega') {
        setUploadingImg(false);
      } else {
        setUploadingCargaGasolinaImg(false);
      }
    }
  }

  async function handleCargaGasolinaVoucherImage(event: ChangeEvent<HTMLInputElement>) {
    const [file] = Array.from(event.target.files ?? []);
    if (!file) return;

    setUploadingCargaGasolinaImg(true);
    try {
      const url = await uploadFileToMinio(file, 'cargas-gasolina');
      setCargaGasolinaImgFacturavou(url);
      setCargaGasolinaError('');
      setCargaGasolinaMensaje('');
    } catch (err: unknown) {
      setCargaGasolinaMensaje('');
      setCargaGasolinaError((err as { message?: string })?.message ?? 'No se pudo subir la foto del voucher o factura.');
    } finally {
      setUploadingCargaGasolinaImg(false);
      event.target.value = '';
    }
  }

  function removeCargaGasolinaVoucherImage() {
    setCargaGasolinaImgFacturavou('');
    setCargaGasolinaError('');
    setCargaGasolinaMensaje('');
  }

  async function handleCapturePhoto() {
    if (!cameraTarget) return;

    const video = cameraVideoRef.current;
    if (!video || !video.videoWidth || !video.videoHeight) {
      setCameraError('La cámara aún no está lista para tomar la foto.');
      return;
    }

    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const context = canvas.getContext('2d');
    if (!context) {
      setCameraError('No se pudo preparar la captura de la foto.');
      return;
    }

    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    const blob = await new Promise<Blob | null>(resolve => {
      canvas.toBlob(resolve, 'image/jpeg', 0.92);
    });

    if (!blob) {
      setCameraError('No se pudo capturar la imagen desde la cámara.');
      return;
    }

    const file = new File([blob], `${cameraTarget}-${Date.now()}.jpg`, {
      type: 'image/jpeg',
      lastModified: Date.now(),
    });

    await uploadCapturedPhoto(file, cameraTarget);
  }

  async function handleSubmitPaquete(event: FormEvent) {
    event.preventDefault();
    if (!viaje) return;

    setLoading(true);
    try {
      const viajeGuardado = await saveViaje({
        ...viaje,
        paquete: {
          ...(viaje.paquete ?? createEmptyPaquete()),
          ...paquete,
          imagenes: paquete.imagenes,
        },
        estado: 'pendiente',
      });
      setViajes(prev => upsertViajeOperativo(prev, viajeGuardado));
      if (viajeGuardado.tipoEntrega === 'transferencia') {
        applySelectedViaje(viajeGuardado, 1);
        await iniciarViaje({
          viajeSeleccionado: viajeGuardado,
          habilitarTrackingContinuo: false,
          siguientePaso: 3,
        });
      } else {
        applySelectedViaje(viajeGuardado, 2);
      }
      setError('');
    } catch (err: unknown) {
      setError((err as { message?: string })?.message ?? 'No se pudo registrar la recepcion del paquete.');
    } finally {
      setLoading(false);
    }
  }

  async function handleGuardarCargaGasolina(event: FormEvent) {
    event.preventDefault();

    if (!cargaGasolinaVehiculoId) {
      setCargaGasolinaError('Selecciona un vehículo para registrar la carga.');
      return;
    }

    const cantidadGalones = Number(cargaGasolinaCantidadGalones);
    const totalPagado = Number(cargaGasolinaTotalPagado);

    if (!cantidadGalones || cantidadGalones <= 0) {
      setCargaGasolinaError('Ingresa una cantidad de galones mayor a cero.');
      return;
    }

    if (!totalPagado || totalPagado <= 0) {
      setCargaGasolinaError('Ingresa un total pagado mayor a cero.');
      return;
    }

    if (cargaGasolinaFormaPago === 'tc') {
      if (!cargaGasolinaNumeroVoucher.trim()) {
        setCargaGasolinaError('Ingresa el número de voucher.');
        return;
      }

      if (!/^\d{4}$/.test(cargaGasolinaUltimosCuatro.trim())) {
        setCargaGasolinaError('Ingresa los últimos 4 dígitos de la tarjeta.');
        return;
      }
    }

    if (!cargaGasolinaImgFacturavou.trim()) {
      setCargaGasolinaError('Debes subir la foto del voucher o factura antes de guardar la carga.');
      return;
    }

    try {
      setCargaGasolinaSaving(true);
      await createCargaGasolina({
        vehiculoId: cargaGasolinaVehiculoId,
        formaPago: cargaGasolinaFormaPago,
        ultimosCuatroTarjeta: cargaGasolinaFormaPago === 'tc' ? cargaGasolinaUltimosCuatro.trim() : undefined,
        numeroVoucher: cargaGasolinaNumeroVoucher.trim(),
        imgFacturavou: cargaGasolinaImgFacturavou || undefined,
        cantidadGalones,
        totalPagado,
      });
      setCargaGasolinaUltimosCuatro('');
      setCargaGasolinaNumeroVoucher('');
      setCargaGasolinaImgFacturavou('');
      setCargaGasolinaCantidadGalones('');
      setCargaGasolinaTotalPagado('');
      setCargaGasolinaError('');
      setCargaGasolinaMensaje('Carga de gasolina registrada correctamente.');
      setCargasGasolinaPagina(1);
      await refreshCargasGasolina(1);
    } catch (err: unknown) {
      setCargaGasolinaMensaje('');
      setCargaGasolinaError((err as { message?: string })?.message ?? 'No se pudo registrar la carga de gasolina.');
    } finally {
      setCargaGasolinaSaving(false);
    }
  }

  function obtenerCoordenadaActual() {
    return new Promise<{ lat: number; lng: number; timestamp: number }>((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocalizacion no disponible en este dispositivo.'));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        pos =>
          resolve({
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
            timestamp: pos.timestamp,
          }),
        err => reject(new Error(`Error GPS: ${err.message}`)),
        { enableHighAccuracy: true, maximumAge: 0, timeout: 10000 },
      );
    });
  }

  async function iniciarViaje(options?: {
    viajeSeleccionado?: Viaje;
    habilitarTrackingContinuo?: boolean;
    siguientePaso?: Paso;
  }) {
    const viajeObjetivo = options?.viajeSeleccionado ?? viaje;
    const habilitarTrackingContinuo = options?.habilitarTrackingContinuo ?? true;
    const siguientePaso = options?.siguientePaso ?? 2;
    if (!viajeObjetivo || iniciandoViaje) {
      return;
    }

    if (!navigator.geolocation) {
      setGeoError('Geolocalizacion no disponible en este dispositivo.');
      return;
    }

    setIniciandoViaje(true);
    setGeoError('');
    detenerGPS();
    const viajeActualizado: Viaje = { ...viajeObjetivo, estado: 'en_curso' };
    try {
      const coordenadaInicio = await obtenerCoordenadaActual();
      const viajeGuardado = await saveViaje(viajeActualizado, {
        coordenadaInicio,
      });
      latestTrackingCoordRef.current = coordenadaInicio;
      lastSentTrackingTimestampRef.current = coordenadaInicio.timestamp;
      setViajes(prev => upsertViajeOperativo(prev, viajeGuardado));
      applySelectedViaje(viajeGuardado, siguientePaso);
      setCoordActual({ lat: coordenadaInicio.lat, lng: coordenadaInicio.lng });
      setSyncError('');
    } catch (err: unknown) {
      const message =
        (err as { message?: string })?.message ??
        'No se pudo iniciar el viaje en el servidor.';
      setGeoError(message);
      setIniciandoViaje(false);
      return;
    }

    setIniciandoViaje(false);

    if (habilitarTrackingContinuo) {
      trackingIntervalRef.current = window.setInterval(() => {
        void transmitirUltimaCoordenada(viajeActualizado.id);
      }, 10000);

      watchIdRef.current = navigator.geolocation.watchPosition(
        pos => {
          const coord = {
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
            timestamp: pos.timestamp,
          };
          latestTrackingCoordRef.current = coord;
          setCoordActual({ lat: coord.lat, lng: coord.lng });
        },
        err => setGeoError(`Error GPS: ${err.message}`),
        { enableHighAccuracy: true, maximumAge: 5000, timeout: 10000 },
      );
    }

    const externalUrl = getExternalMapUrl(viajeObjetivo);
    if (habilitarTrackingContinuo && externalUrl) {
      window.open(externalUrl, '_blank', 'noopener,noreferrer');
    }
  }

  async function transmitirUltimaCoordenada(viajeId: string): Promise<Viaje | undefined> {
    const latestCoord = latestTrackingCoordRef.current;
    if (
      !latestCoord
      || isSendingTrackingRef.current
      || lastSentTrackingTimestampRef.current === latestCoord.timestamp
    ) {
      return undefined;
    }

    isSendingTrackingRef.current = true;
    try {
      const viajeTransmitido = await agregarCoordenada(viajeId, latestCoord);
      if (!viajeTransmitido) return undefined;

      lastSentTrackingTimestampRef.current = latestCoord.timestamp;
      setCoordActual(
        viajeTransmitido.coordenadaActual
          ? {
              lat: viajeTransmitido.coordenadaActual.lat,
              lng: viajeTransmitido.coordenadaActual.lng,
            }
          : null,
      );
      setViaje(actual => (actual?.id === viajeTransmitido.id ? viajeTransmitido : actual));
      setViajes(prev => upsertViajeOperativo(prev, viajeTransmitido));
      setGeoError('');
      return viajeTransmitido;
    } catch (err: unknown) {
      setGeoError(
        (err as { message?: string })?.message ??
          'No se pudo transmitir la ubicacion al servidor.',
      );
      return undefined;
    } finally {
      isSendingTrackingRef.current = false;
    }
  }

  function detenerGPS() {
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    if (trackingIntervalRef.current !== null) {
      window.clearInterval(trackingIntervalRef.current);
      trackingIntervalRef.current = null;
    }
    latestTrackingCoordRef.current = null;
    lastSentTrackingTimestampRef.current = null;
    isSendingTrackingRef.current = false;
  }

  async function avanzarAPaso3() {
    if (!viaje || preparandoEntrega) {
      return;
    }

    setPreparandoEntrega(true);
    try {
      await transmitirUltimaCoordenada(viaje.id);
      detenerGPS();
      setPaso(3);
      setError('');
    } finally {
      setPreparandoEntrega(false);
    }
  }

  useEffect(() => () => detenerGPS(), []);

  async function handleImagenEntrega(event: ChangeEvent<HTMLInputElement>) {
    const files = Array.from(event.target.files ?? []);
    if (files.length === 0) return;
    setUploadingImg(true);
    try {
      const urls = await Promise.all(files.map(file => uploadFileToMinio(file, 'entregas')));
      setImagenesEntrega(current => [...current, ...urls]);
      setError('');
      event.target.value = '';
    } catch (err: unknown) {
      setError((err as { message?: string })?.message ?? 'No se pudieron subir las imágenes de entrega.');
    } finally {
      setUploadingImg(false);
    }
  }

  function removeImagenEntrega(index: number) {
    setImagenesEntrega(current => current.filter((_, currentIndex) => currentIndex !== index));
  }

  async function handleFirmarGuiaPdf(input: PdfSignatureInput) {
    const guiaIndex = signatureGuiaIndex ?? 0;
    const guiaParaFirmar = guiasPdfViaje[guiaIndex];

    if (!viaje || !guiaParaFirmar) {
      setError('La entrega no tiene una guía PDF disponible para firmar.');
      return;
    }

    setSigningPdf(true);
    try {
      const result = await firmarGuiaPdf({
        guiaPdfUrl: guiaParaFirmar.url,
        signatureContent: input.signatureContent,
        fileName: guiaParaFirmar.nombreArchivo || `${viaje.paquete?.nombre || 'guia'}-${guiaIndex + 1}.pdf`,
        pageNumber: input.pageNumber,
        pdfX: input.pdfX,
        pdfY: input.pdfY,
        pdfWidth: input.pdfWidth,
        pdfHeight: input.pdfHeight,
      });

      const nextGuiasFirmadas = [...guiasFirmadasViaje];
      nextGuiasFirmadas[guiaIndex] = {
        url: result.pdfGuiaFirmadoUrl,
        nombreArchivo: result.pdfGuiaFirmadoNombreArchivo,
        numeroFactura: guiaParaFirmar.numeroFactura,
        direccion: guiaParaFirmar.direccion,
      };

      const nextViaje: Viaje = {
        ...viaje,
        pdfGuiaFirmadoUrl: nextGuiasFirmadas[0]?.url ?? result.pdfGuiaFirmadoUrl,
        guiasFirmadas: nextGuiasFirmadas,
      };

      setViaje(nextViaje);
      setViajes(prev => upsertViajeOperativo(prev, nextViaje));
      setGuiasFirmadasPendientes(nextGuiasFirmadas);
      setSignedPdfName(result.pdfGuiaFirmadoNombreArchivo);
      setSignedPdfNames(prev => ({
        ...prev,
        [guiaIndex]: result.pdfGuiaFirmadoNombreArchivo,
      }));
      setError('');
      setSyncError('');
      setSignatureModalOpen(false);
      setSignatureGuiaIndex(null);
      alert('La guía se firmó y se subió correctamente.');
    } catch (err: unknown) {
      setError((err as { message?: string })?.message ?? 'No se pudo firmar la guía PDF.');
    } finally {
      setSigningPdf(false);
    }
  }

  function abrirFirmaGuia(index: number) {
    setSignatureGuiaIndex(index);
    setTermsAccepted(false);
    setTermsModalOpen(true);
  }

  async function handleFinalizarViaje(event: FormEvent) {
    event.preventDefault();
    if (!viaje) return;
    const guiasDevolucion = devolucionActiva
      ? buildGuideReturnPayload(guiasPdfViaje, guiasDevueltasIndexSet, itemsDevolucionSeleccionados)
      : [];
    const viajeConFirmas: Viaje = {
      ...viaje,
      pdfGuiaFirmadoUrl: guiasFirmadasViaje[0]?.url ?? viaje.pdfGuiaFirmadoUrl,
      guiasFirmadas: guiasFirmadasViaje,
    };

    if (devolucionActiva && guiasDevolucion.length === 0) {
      setError('Debes seleccionar al menos una guía completa o un producto para marcarlo como devolución.');
      return;
    }

    if (firmaGuiaObligatoria && !hasSignedRequiredGuides(viajeConFirmas, devolucionActiva ? guiasDevueltasIndexSet : undefined)) {
      setError(
        devolucionActiva
          ? 'Debes firmar todas las guías que no marcaste como devolución completa.'
          : hasMultipleGuias
          ? 'Debes firmar todas las guías PDF antes de cerrar la entrega.'
          : 'Debes firmar la guía PDF antes de cerrar la entrega.',
      );
      return;
    }

    if (requiereEvidenciaEntrega && imagenesEntrega.length === 0) {
      setError('Debes tomar al menos una foto o subir un archivo de evidencia antes de cerrar la entrega.');
      return;
    }

    const observacionesFinal = observaciones.trim();
    if (isTransferencia && !observacionesFinal) {
      setError('Debes ingresar observaciones para finalizar una transferencia.');
      return;
    }

    const paymentNotApplicable = isTransferencia || (devolucionActiva && !devolucionPermiteCobro);
    const formaPagoFinal = paymentNotApplicable ? '' : pagoCompletoBloqueado ? 'completo' : formaPago;
    if (!paymentNotApplicable) {
      if (!formaPagoFinal) {
        setError('Debes seleccionar una forma de pago.');
        return;
      }

      if (!isValidPaymentAmount(valorPago)) {
        setError('Debes ingresar un valor valido de hasta 12 caracteres usando solo numeros y coma.');
        return;
      }

      if ((formaPagoFinal === 'transferencia' || formaPagoFinal === 'cheque') && !bancoPago.trim()) {
        setError('Debes ingresar el banco del pago registrado.');
        return;
      }

      if ((formaPagoFinal === 'transferencia' || formaPagoFinal === 'cheque' || formaPagoFinal === 'nota_credito') && !isValidPaymentReference(referenciaPago)) {
        setError('Debes ingresar una referencia valida de hasta 40 caracteres alfanumericos o guion.');
        return;
      }
    }

    const montoRegistrado = paymentNotApplicable ? 0 : parseAmountInput(valorPago);
    if (!paymentNotApplicable && typeof montoRegistrado !== 'number') {
      setError('No se pudo determinar el monto a registrar para la entrega.');
      return;
    }

    setLoading(true);
    setError('');
    try {
      detenerGPS();
      const coordenadaFin = await obtenerCoordenadaActual();
      const referenciaPagoGuardada = paymentNotApplicable
        ? ''
        : buildPaymentReference(
            formaPagoFinal,
            formatAmountInput(montoRegistrado),
            referenciaPago,
          );
      const pagoEntrega = paymentNotApplicable
        ? undefined
        : formaPagoFinal === 'nota_credito'
        ? {
            tipo: formaPagoFinal,
            valorNotaCredito: montoRegistrado,
            numeroNotaCredito: referenciaPago.trim(),
          }
        : formaPagoFinal === 'transferencia'
        ? {
            tipo: formaPagoFinal,
            monto: montoRegistrado,
            banco: bancoPago.trim(),
            numeroTransferencia: referenciaPago.trim(),
          }
        : formaPagoFinal === 'cheque'
        ? {
            tipo: formaPagoFinal,
            monto: montoRegistrado,
            banco: bancoPago.trim(),
            numeroCheque: referenciaPago.trim(),
          }
        : formaPagoFinal
        ? {
            tipo: formaPagoFinal,
            monto: montoRegistrado,
          }
        : undefined;
      const viajeTerminado: Viaje = {
        ...viajeConFirmas,
        estado: 'finalizado',
        esDevolucion: devolucionActiva,
        direccionEntrega: devolucionActiva ? '' : direccionEntrega,
        formaPago: formaPagoFinal || undefined,
        pagoEntrega,
        referenciaPago: referenciaPagoGuardada,
        kilometrosRecorridos: 0,
        imagenEntrega: imagenesEntrega[0] ?? undefined,
        imagenesEntrega,
        observaciones: observacionesFinal,
        fechaFin: Date.now(),
      };
      const viajeGuardado = await saveViaje(viajeTerminado, {
        coordenadaFin,
        guiasDevolucion: guiasDevolucion.length > 0 ? guiasDevolucion : undefined,
      });
      setViajes(prev => upsertViajeOperativo(prev, viajeGuardado));
      applySelectedViaje(null, 1);
      setSection('historial');
      setSyncError('');
      setDireccionEntrega('');
      setEsDevolucion(false);
      setFormaPago('');
      setValorPago('');
      setReferenciaPago('');
      setBancoPago('');
      setKmRecorridos(0);
      setImagenesEntrega([]);
      setObservaciones('');
      setMotivoDevolucion('');
      setGasolina(createEmptyGasolina());
      setSignedPdfName('');
      setSignedPdfNames({});
      setGuiasFirmadasPendientes([]);
      setGuiasDevolucionSeleccionadas([]);
      setItemsDevolucionSeleccionados({});
      setSignatureModalOpen(false);
      setSignatureGuiaIndex(null);
      setTermsModalOpen(false);
      setTermsAccepted(false);
      setTermsPreviewOpen(false);
      observacionesAutoDevolucionRef.current = '';
      valorPagoAutoRef.current = '';
      alert('Entrega finalizada exitosamente.');
      void refreshRemoteState();
    } catch (err: unknown) {
      setError((err as { message?: string })?.message ?? 'Error al finalizar la entrega.');
    } finally {
      setLoading(false);
    }
  }

  const inputCls =
    'w-full rounded-[14px] border border-[var(--freisa-border)] bg-white px-3 py-2.5 text-sm text-stone-700 outline-none transition focus:border-[var(--freisa-red)] focus:ring-4 focus:ring-[var(--freisa-red)]/10';
  const labelCls = 'mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-stone-500';

  return (
    <>
      <PanelShell
        roleLabel="Conductor"
        pageTitle="Panel de ruta"
        pageSubtitle="Vista operativa para recepción, viaje, cierre y consulta del historial del conductor."
        currentLabel={SECTION_COPY[section].label}
        currentDetail={SECTION_COPY[section].detail}
        userName={conductor?.nombre ?? user?.profile?.razon_social ?? user?.username ?? 'Conductor'}
        userMeta={conductor?.placa ?? 'Sin vehículo asignado'}
        navItems={navItems}
        activeKey={section}
        onSelect={key => setSection(key as Section)}
        notifications={dashboardStats.notifications}
        onLogout={logout}
        viewModeToggle
        aparienciaPersonalizable
      >
        {syncError && (
          <div className="mb-6 rounded-[18px] border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700 shadow-sm">
            {syncError}
          </div>
        )}

        {shouldShowNotificationBanner && (
          <div className="mb-6 rounded-[22px] border border-sky-200 bg-sky-50/90 px-5 py-4 text-sm text-sky-900 shadow-sm">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-sky-600">Notificaciones móviles</p>
                <p className="mt-2 text-sm leading-6 text-sky-900">
                  {notificationPermission === 'denied'
                    ? 'Las notificaciones están bloqueadas. Actívalas en la configuración del navegador para recibir una alerta por cada entrega que te asigne el administrador.'
                    : notificationPermission === 'unsupported'
                    ? 'Este dispositivo o navegador no expone notificaciones web en esta sesión segura. Si usas el celular, abre la aplicación/PWA con HTTPS para habilitarlas.'
                    : 'Activa las notificaciones para recibir una alerta en tu celular cada vez que el administrador te asigne una nueva entrega.'}
                </p>
              </div>
              {notificationPermission === 'default' && (
                <button
                  type="button"
                  onClick={() => void handleEnableAssignmentNotifications()}
                  disabled={requestingNotificationPermission}
                  className="inline-flex items-center justify-center rounded-[14px] bg-sky-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {requestingNotificationPermission ? 'Solicitando permiso...' : 'Activar notificaciones'}
                </button>
              )}
            </div>
            {notificationError && (
              <p className="mt-3 text-sm text-sky-800">{notificationError}</p>
            )}
          </div>
        )}

          {!conductor ? (
            <div className="rounded-[30px] border border-dashed border-stone-300 bg-white/70 p-12 text-center text-stone-500">
              <div className="text-5xl">⚠️</div>
              <p className="mt-4 text-lg font-medium text-stone-700">Tu cuenta no esta vinculada a ningun conductor.</p>
              <p className="mt-2 text-sm">Contacta al administrador para activar tu perfil operativo.</p>
            </div>
          ) : (
            <>
              {section === 'dashboard' && (
                <section className="mb-6 space-y-6">
                  <div className="grid gap-4 xl:grid-cols-4">
                    {dashboardStats.summary.map(stat => (
                      <DashboardStatCard
                        key={stat.label}
                        icon={stat.icon}
                        label={stat.label}
                        value={stat.value}
                        trend={stat.trend}
                        trendTone={stat.trendTone}
                      />
                    ))}
                  </div>

                  <div className="grid gap-6 xl:grid-cols-[1.2fr_0.8fr]">
                    <WeeklyBarChart
                      title="Envíos semanal"
                      subtitle="Rutas iniciadas o cerradas usando los viajes ya visibles en tu panel."
                      data={dashboardStats.weeklySeries}
                    />
                    <ActivityFeed
                      title="Actividad reciente"
                      subtitle="Últimos movimientos de tus entregas asignadas y finalizadas."
                      items={dashboardStats.recentActivity}
                    />
                  </div>
                </section>
              )}

              {section === 'actual' && (
                <section className="space-y-6">
                  <div className="flex flex-wrap gap-3">
                    {(isTransferencia ? [1, 3] : [1, 2, 3] as Paso[]).map(p => (
                      <div key={p} className="flex min-w-[180px] flex-1 items-center gap-3 rounded-[24px] border border-white/80 bg-white/85 px-4 py-4 shadow-[0_20px_50px_rgba(120,113,108,0.08)]">
                        <div
                          className={`flex h-10 w-10 items-center justify-center rounded-full text-sm font-bold ${
                            paso === p
                              ? 'bg-emerald-600 text-white'
                              : paso > p
                              ? 'bg-emerald-100 text-emerald-800'
                              : 'bg-stone-200 text-stone-500'
                          }`}
                        >
                          {p}
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-stone-900">
                            {p === 1 ? 'Recibir paquete' : p === 2 ? 'En camino' : 'Finalizar'}
                          </p>
                          <p className="text-xs text-stone-500">
                            {p === 1 ? 'Evidencia inicial' : p === 2 ? 'Mapa y GPS' : 'Cierre y soportes'}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>

                  {!viaje ? (
                    <div className="rounded-[30px] border border-dashed border-stone-300 bg-white/70 p-12 text-center text-stone-500">
                      <div className="text-5xl">📭</div>
                      <p className="mt-4 text-lg font-medium text-stone-700">No tienes un viaje activo seleccionado.</p>
                      <p className="mt-2 text-sm">Revisa el menu lateral en “Entregas asignadas” para abrir una pendiente.</p>
                    </div>
                  ) : (
                    <>
                      {paso === 1 && (
                        <div className="rounded-[32px] border border-white/80 bg-white/85 p-6 shadow-[0_24px_60px_rgba(120,113,108,0.12)]">
                          <div className="mb-4 flex items-start justify-between gap-4">
                            <div>
                              <h3 className="text-xl font-semibold text-stone-950">Paso 1 - Recibir paquete</h3>
                              {viaje.tipoEntrega !== 'transferencia' && (
                                <p className="mt-1 text-sm text-stone-500">Cliente: {viaje.paquete?.cliente?.nombre || '—'}</p>
                              )}
                              <p className="mt-1 text-sm text-stone-500">Vendedor: {viaje.vendedor?.nombre || viaje.vendedor?.username || '—'}</p>
                              <p className="mt-1 text-sm text-stone-500">Sucursal: {viaje.vendedor?.sucursal?.nombre || viaje.sucursal?.nombre || '—'}</p>
                            </div>
                            <span className={`rounded-full px-3 py-1 text-xs font-semibold ${estadoBadge(viaje)}`}>
                              {estadoLabel(viaje)}
                            </span>
                          </div>

                          <ClienteContactoResumen viaje={viaje} className="rounded-[24px] bg-stone-50/90 p-4" />

                          <div className="mt-4 rounded-[24px] border border-amber-200 bg-amber-50/80 p-4 text-sm text-stone-700">
                            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-amber-700">{isTransferencia ? 'Resumen de transferencia' : 'Guía de cobro'}</p>
                            <GuiaCobroResumen viaje={viaje} className="mt-3" separarDescripcionFacturas />
                            {!isTransferencia && (
                              <p className="mt-3 rounded-2xl bg-white/80 px-3 py-2 text-sm text-stone-600">{pagoPactadoHint}</p>
                            )}
                          </div>

                          <form onSubmit={handleSubmitPaquete} className="mt-4 space-y-4">
                            <div>
                              <label className={labelCls}>Fotos del paquete recibido (opcional)</label>
                              {preferCameraCapture ? (
                                <div className="space-y-3">
                                  <div className="grid gap-3 sm:grid-cols-2">
                                    <button
                                      type="button"
                                      onClick={() => void openCameraCapture('paquete')}
                                      disabled={uploadingPaquete}
                                      className="w-full rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-700 transition hover:bg-emerald-100 disabled:opacity-60"
                                    >
                                      Abrir cámara del dispositivo
                                    </button>
                                    <label className={`flex w-full cursor-pointer items-center justify-center rounded-2xl border border-stone-200 bg-white px-4 py-3 text-sm font-semibold text-stone-700 transition hover:bg-stone-50 ${uploadingPaquete ? 'pointer-events-none opacity-60' : ''}`}>
                                      Seleccionar archivos
                                      <input
                                        type="file"
                                        accept="image/*"
                                        multiple
                                        onChange={handleImagenesPaquete}
                                        disabled={uploadingPaquete}
                                        className="hidden"
                                      />
                                    </label>
                                  </div>
                                  <p className="text-xs text-stone-400">Puedes tomar una foto en el popup o seleccionar imágenes desde tu dispositivo. Ambas opciones se suben a MinIO.</p>
                                </div>
                              ) : (
                                <input
                                  type="file"
                                  accept="image/*"
                                  multiple
                                  onChange={handleImagenesPaquete}
                                  className="block w-full text-sm text-stone-500 file:mr-3 file:rounded-2xl file:border-0 file:bg-emerald-50 file:px-3 file:py-2 file:text-emerald-700 hover:file:bg-emerald-100"
                                />
                              )}
                              {uploadingPaquete && <p className="mt-2 text-xs text-stone-400">Subiendo imagenes a MinIO...</p>}
                              {preferCameraCapture && cameraError && (!cameraTarget || cameraTarget === 'paquete') && <p className="mt-2 text-xs text-red-500">{cameraError}</p>}
                              {paquete.imagenes.length > 0 && (
                                <div className="mt-3 flex flex-wrap gap-2">
                                  {paquete.imagenes.map((img, index) => (
                                    <div key={`${img.slice(0, 20)}-${index}`} className="relative">
                                      <img src={img} alt="paquete" className="h-16 w-16 rounded-lg border border-stone-200 object-cover" />
                                      <button
                                        type="button"
                                        onClick={() =>
                                          setPaquete(prev => ({
                                            ...prev,
                                            imagenes: prev.imagenes.filter((_, candidateIndex) => candidateIndex !== index),
                                          }))
                                        }
                                        className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-xs text-white"
                                      >
                                        x
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>

                            {error && (
                              <p className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                                {error}
                              </p>
                            )}

                            {isTransferencia && geoError && (
                              <p className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                                {geoError}
                              </p>
                            )}

                            <button
                              type="submit"
                              disabled={loading || uploadingPaquete || iniciandoViaje}
                              className="w-full rounded-2xl bg-emerald-600 py-3 text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:opacity-50"
                            >
                              {loading
                                ? 'Guardando...'
                                : iniciandoViaje
                                ? 'Iniciando viaje...'
                                : isTransferencia
                                ? 'Iniciar viaje'
                                : 'Continuar al viaje'}
                            </button>
                          </form>
                        </div>
                      )}

                      {paso === 2 && (
                        <div className="rounded-[32px] border border-white/80 bg-white/85 p-6 shadow-[0_24px_60px_rgba(120,113,108,0.12)]">
                          <h3 className="text-xl font-semibold text-stone-950">Paso 2 - En camino</h3>

                          {viaje.estado === 'pendiente' ? (
                            <div className="space-y-3 py-6 text-center">
                              <p className="text-sm text-stone-600">
                                Presiona Iniciar viaje para abrir Google Maps en una nueva vista y comenzar el seguimiento en tiempo real desde el sistema.
                              </p>
                              {geoError && (
                                <p className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                                  {geoError}
                                </p>
                              )}
                              {iniciandoViaje && (
                                <div className="rounded-2xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-700">
                                  Estamos abriendo Google Maps, cargando coordenadas y preparando el mapa. Por favor espera...
                                </div>
                              )}
                              <button
                                onClick={() => {
                                  void iniciarViaje();
                                }}
                                disabled={iniciandoViaje}
                                className="rounded-2xl bg-emerald-600 px-8 py-3 text-lg font-bold text-white transition hover:bg-emerald-700 disabled:opacity-60"
                              >
                                {iniciandoViaje ? 'Cargando coordenadas...' : 'Iniciar viaje'}
                              </button>
                            </div>
                          ) : (
                            <div className="space-y-4">
                              <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                                <div className="flex items-center justify-between gap-3">
                                  <span className="rounded-full bg-emerald-50 px-3 py-1 text-sm font-medium text-emerald-700 animate-pulse">
                                    Transmitiendo ubicacion
                                  </span>
                                  {coordActual && (
                                    <span className="text-xs text-stone-400">
                                      {coordActual.lat.toFixed(5)}, {coordActual.lng.toFixed(5)}
                                    </span>
                                  )}
                                </div>
                                <button
                                  type="button"
                                  onClick={() => {
                                    void avanzarAPaso3();
                                  }}
                                  disabled={preparandoEntrega || loading || signingPdf}
                                  className="rounded-2xl border border-amber-300 bg-amber-50 px-5 py-2.5 text-sm font-semibold text-amber-800 transition hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-60"
                                >
                                  {preparandoEntrega ? 'Preparando entrega...' : 'Finalizar viaje'}
                                </button>
                              </div>

                              <p className="text-xs text-stone-500">
                                Si Google Maps se abrió fuera de la app, usa Finalizar viaje para continuar al paso 3 y registrar la entrega desde aquí.
                              </p>

                              {geoError && (
                                <p className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                                  {geoError}
                                </p>
                              )}

                              {!firmaOverlayActiva && (
                                <MapaConductor
                                  coordenadas={viaje.coordenadas}
                                  coordenadaActual={coordenadaMapaActual}
                                  coordenadaInicio={viaje.coordenadaInicio}
                                  coordenadaFin={viaje.coordenadaFin}
                                  puntosIntermedios={viaje.puntosIntermedios}
                                />
                              )}

                              {hasMultipleGuias && !isEnvio && (
                                <div className="rounded-[24px] border border-stone-200 bg-stone-50/80 p-4">
                                  <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                                    <div>
                                      <p className="text-sm font-semibold text-stone-900">Guías para firmar</p>
                                      <p className="text-xs text-stone-500">Firma cada factura antes de registrar la entrega final.</p>
                                    </div>
                                    <span className="text-xs font-semibold text-stone-500">
                                      {guiasFirmadasViaje.filter(guia => guia.url).length}/{guiasPdfViaje.length} firmadas
                                    </span>
                                  </div>

                                  <div className="mt-4 grid gap-3 lg:grid-cols-2">
                                    {guiasPdfViaje.map((guia, index) => {
                                      const guiaFirmada = guiasFirmadasViaje[index];
                                      return (
                                        <div key={`${guia.url}-${index}`} className="rounded-2xl border border-white bg-white px-4 py-3 text-sm text-stone-600">
                                          <div className="flex items-start justify-between gap-3">
                                            <div>
                                              <p className="font-semibold text-stone-900">Guía {index + 1}</p>
                                              <p className="mt-1 text-xs text-stone-500">Factura: {guia.numeroFactura || '—'}</p>
                                              <p className="mt-1 text-xs text-stone-500">Dirección: {guia.direccion || '—'}</p>
                                            </div>
                                            <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${guiaFirmada?.url ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
                                              {guiaFirmada?.url ? 'Firmada' : 'Pendiente'}
                                            </span>
                                          </div>
                                          <div className="mt-3 flex flex-wrap gap-2">
                                            <a href={guia.url} target="_blank" rel="noreferrer" className="rounded-full bg-stone-100 px-3 py-1.5 font-medium text-stone-700 transition hover:bg-stone-200">
                                              Ver guía
                                            </a>
                                            <button
                                              type="button"
                                              onClick={() => abrirFirmaGuia(index)}
                                              disabled={signingPdf || loading}
                                              className="rounded-full bg-stone-950 px-3 py-1.5 font-medium text-white transition hover:bg-stone-700 disabled:opacity-50"
                                            >
                                              {guiaFirmada?.url ? 'Volver a firmar' : 'Firmar'}
                                            </button>
                                            {guiaFirmada?.url && (
                                              <a href={guiaFirmada.url} target="_blank" rel="noreferrer" className="rounded-full bg-emerald-50 px-3 py-1.5 font-medium text-emerald-700 transition hover:bg-emerald-100">
                                                Ver firmada
                                              </a>
                                            )}
                                          </div>
                                          {signedPdfNames[index] && (
                                            <p className="mt-2 text-xs text-stone-500">Archivo firmado: {signedPdfNames[index]}</p>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}

                              <button
                                onClick={avanzarAPaso3}
                                className="w-full rounded-2xl bg-red-500 py-3 text-sm font-semibold text-white transition hover:bg-red-600"
                              >
                                Llegue al destino - Finalizar entrega
                              </button>
                              {getExternalMapUrl(viaje) && (
                                <a
                                  href={getExternalMapUrl(viaje)}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="mt-3 flex w-full items-center justify-center rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-700 transition hover:bg-emerald-100"
                                >
                                  Abrir destino configurado
                                </a>
                              )}
                              <button
                                type="button"
                                onClick={() => setSection('asignadas')}
                                className="mt-3 w-full rounded-2xl border border-stone-200 bg-white px-4 py-3 text-sm font-semibold text-stone-700 transition hover:bg-stone-50"
                              >
                                Volver a Entregas Asignadas
                              </button>
                            </div>
                          )}

                          <ClienteContactoResumen viaje={viaje} className="mt-4 rounded-[24px] bg-stone-50/90 p-4" />
                        </div>
                      )}

                      {paso === 3 && (
                        <div className="rounded-[32px] border border-white/80 bg-white/85 p-6 shadow-[0_24px_60px_rgba(120,113,108,0.12)]">
                          <h3 className="text-xl font-semibold text-stone-950">Paso 3 - Registrar entrega</h3>

                          <ClienteContactoResumen viaje={viaje} className="mb-4 mt-4 rounded-[24px] bg-stone-50/90 p-4" />

                          <div className="mb-4 rounded-[24px] border border-blue-200 bg-blue-50/80 p-4 text-sm text-stone-700">
                            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-blue-700">{isTransferencia ? 'Resumen de transferencia' : 'Resumen de pago'}</p>
                            {permiteDevolucion && (
                              <div className="mt-3 rounded-2xl bg-white/80 p-3">
                                <label className="flex items-center gap-3 text-sm font-medium text-stone-700">
                                  <input
                                    type="checkbox"
                                    checked={devolucionActiva}
                                    onChange={event => handleToggleDevolucion(event.target.checked)}
                                    className="h-4 w-4 rounded border-stone-300 text-red-600 focus:ring-red-200"
                                  />
                                  Marcar entrega como devolución
                                </label>
                              </div>
                            )}
                            {devolucionActiva ? (
                              <div className="mt-3 space-y-3">
                                <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                                  La dirección de entrega no se guardará. Si la devolución es parcial, podrás registrar el cobro restante; si es total, el pago quedará en blanco.
                                </div>
                                <div className="rounded-2xl bg-white/80 p-3 md:max-w-xl">
                                  <label className="text-xs uppercase tracking-[0.16em] text-stone-500">Motivo de devolución</label>
                                  <select
                                    value={motivoDevolucion}
                                    onChange={event => setMotivoDevolucion(event.target.value)}
                                    className="mt-2 w-full rounded-2xl border border-stone-200 bg-white px-3 py-2.5 text-sm text-stone-700 outline-none transition focus:border-red-400 focus:ring-4 focus:ring-red-100"
                                  >
                                    <option value="">Selecciona un motivo</option>
                                    {MOTIVOS_DEVOLUCION_ENTREGA.map(motivo => (
                                      <option key={motivo} value={motivo}>{motivo}</option>
                                    ))}
                                  </select>
                                  <p className="mt-2 text-xs text-stone-500">El motivo se agrega automáticamente a observaciones y no se envía como un campo independiente.</p>
                                </div>
                                <div className="grid gap-3 md:grid-cols-5">
                                  <div className="rounded-2xl bg-white/80 p-3">
                                    <p className="text-xs uppercase tracking-[0.16em] text-stone-500">Tipo de devolución</p>
                                    <p className="mt-2 text-base font-semibold text-stone-900">{tipoDevolucionSeleccionada === 'parcial' ? 'Parcial' : 'Total'}</p>
                                  </div>
                                  <div className="rounded-2xl bg-white/80 p-3">
                                    <p className="text-xs uppercase tracking-[0.16em] text-stone-500">Guías completas</p>
                                    <p className="mt-2 text-base font-semibold text-stone-900">{guiasDevueltasSeleccionadas.length}/{guiasPdfViaje.length}</p>
                                  </div>
                                  <div className="rounded-2xl bg-white/80 p-3">
                                    <p className="text-xs uppercase tracking-[0.16em] text-stone-500">Productos devueltos</p>
                                    <p className="mt-2 text-base font-semibold text-stone-900">{cantidadProductosDevueltos}</p>
                                  </div>
                                  <div className="rounded-2xl bg-white/80 p-3">
                                    <p className="text-xs uppercase tracking-[0.16em] text-stone-500">Valor devuelto</p>
                                    <p className="mt-2 text-base font-semibold text-stone-900">{formatCurrency(valorGuiasDevueltas)}</p>
                                  </div>
                                  <div className="rounded-2xl bg-white/80 p-3">
                                    <p className="text-xs uppercase tracking-[0.16em] text-stone-500">Valor factura restante</p>
                                    <p className="mt-2 text-base font-semibold text-stone-900">{formatCurrency(totalFacturaAjustado)}</p>
                                  </div>
                                  <div className="rounded-2xl bg-white/80 p-3 md:col-span-2">
                                    <p className="text-xs uppercase tracking-[0.16em] text-stone-500">Cobro por guías firmadas</p>
                                    <p className="mt-2 text-base font-semibold text-stone-900">{formatCurrency(montoCobroGuiasFirmadas)}</p>
                                  </div>
                                </div>
                              </div>
                            ) : isTransferencia ? (
                              <>
                                <GuiaCobroResumen viaje={viaje} className="mt-3" separarDescripcionFacturas />
                                <p className="mt-3 rounded-2xl bg-white/80 px-3 py-2 text-sm text-stone-600">Esta transferencia no requiere forma de pago ni comprobante.</p>
                              </>
                            ) : (
                              <>
                                <GuiaCobroResumen viaje={viaje} className="mt-3" separarDescripcionFacturas />
                                <div className="mt-3 grid gap-3 md:grid-cols-2">
                                <div className="rounded-2xl bg-white/80 p-3">
                                  <p className="text-xs uppercase tracking-[0.16em] text-stone-500">Pago pactado</p>
                                  <p className="mt-2 text-base font-semibold text-stone-900">
                                    {viaje.tipoEntrega === 'entrega' && viaje.paquete?.pago?.tipo === 'completo'
                                      ? 'PAGADO'
                                      : formatPaymentLabel(viaje.paquete?.pago?.tipo)}
                                  </p>
                                </div>
                                {viaje.tipoEntrega === 'entrega' && viaje.paquete?.pago?.tipo === 'completo' ? (
                                  typeof montoCierre === 'number' ? (
                                    <div className="rounded-2xl bg-white/80 p-3">
                                      <p className="text-xs uppercase tracking-[0.16em] text-stone-500">Valor pagado</p>
                                      <p className="mt-2 text-base font-semibold text-stone-900">{formatCurrency(montoCierre)}</p>
                                    </div>
                                  ) : null
                                ) : viaje.paquete?.pago?.tipo === 'parcial' ? (
                                  <div className="rounded-2xl bg-white/80 p-3">
                                    <p className="text-xs uppercase tracking-[0.16em] text-stone-500">Saldo pendiente del paquete</p>
                                    <p className="mt-2 text-base font-semibold text-stone-900">{formatCurrency(saldoPendiente)}</p>
                                  </div>
                                ) : typeof montoCierre === 'number' ? (
                                  <div className="rounded-2xl bg-white/80 p-3">
                                    <p className="text-xs uppercase tracking-[0.16em] text-stone-500">Monto pactado</p>
                                    <p className="mt-2 text-base font-semibold text-stone-900">{formatCurrency(montoCierre)}</p>
                                  </div>
                                ) : null}
                                {typeof montoCierre === 'number' && !(viaje.tipoEntrega === 'entrega' && viaje.paquete?.pago?.tipo === 'completo') && (
                                  <div className="rounded-2xl bg-white/80 p-3 md:col-span-2">
                                    <p className="text-xs uppercase tracking-[0.16em] text-stone-500">Monto a registrar</p>
                                    <p className="mt-2 text-base font-semibold text-stone-900">{formatCurrency(montoCierre)}</p>
                                  </div>
                                )}
                                </div>
                              </>
                            )}
                            <p className="mt-3 rounded-2xl bg-white/80 px-3 py-2 text-sm text-stone-600">{resumenPagoHint}</p>
                          </div>

                          <form onSubmit={handleFinalizarViaje} className="space-y-4">
                            {!devolucionActiva && (
                              <div>
                              <label className={labelCls}>Direccion de entrega</label>
                              <input
                                value={direccionEntrega}
                                onChange={event => setDireccionEntrega(event.target.value)}
                                required
                                className={inputCls}
                                placeholder="Ej: Av. Amazonas N12-34, Quito"
                              />
                              </div>
                            )}

                            {(!devolucionActiva || devolucionPermiteCobro) && !isTransferencia && (
                              <fieldset className="rounded-[24px] border border-stone-200 p-4 space-y-3">
                                <legend className="px-1 text-sm font-semibold text-stone-600">Forma de pago</legend>
                                <div>
                                  <label className={labelCls}>Monto a registrar</label>
                                  <input
                                    value={valorPago}
                                    onChange={event => setValorPago(event.target.value.slice(0, 12))}
                                    className={inputCls}
                                    inputMode="decimal"
                                    maxLength={12}
                                    placeholder="Ej: 25,50"
                                  />
                                </div>
                                <div>
                                  <label className={labelCls}>Metodo de pago</label>
                                  <select
                                    value={formaPago}
                                    onChange={event => {
                                      const nextFormaPago = event.target.value as FormaPagoSeleccion;
                                      setFormaPago(nextFormaPago);
                                      setReferenciaPago('');
                                      setBancoPago('');
                                    }}
                                    className={inputCls}
                                    disabled={pagoCompletoBloqueado}
                                  >
                                    {!isEnvio && <option value="">Selecciona una forma de pago</option>}
                                    {pagoCompletoBloqueado && <option value="completo">Pagado</option>}
                                    {FORMAS_PAGO_ENTREGA.map(tipo => (
                                      <option key={tipo} value={tipo}>
                                        {formatPaymentLabel(tipo)}
                                      </option>
                                    ))}
                                  </select>
                                  {pagoCompletoBloqueado && (
                                    <p className="mt-2 text-xs text-stone-500">
                                      La forma de pago se bloquea porque la entrega fue creada como Pagado.
                                    </p>
                                  )}
                                </div>

                                {(formaPago === 'transferencia' || formaPago === 'cheque') && (
                                  <div className="grid gap-4 md:grid-cols-2">
                                    <div>
                                      <label className={labelCls}>Banco</label>
                                      <input
                                        value={bancoPago}
                                        onChange={event => setBancoPago(event.target.value.slice(0, 120))}
                                        className={inputCls}
                                        maxLength={120}
                                        placeholder="Ej: Pichincha"
                                      />
                                    </div>
                                    <div>
                                      <label className={labelCls}>{formaPago === 'transferencia' ? 'Numero de transferencia' : 'Numero de cheque'}</label>
                                      <input
                                        value={referenciaPago}
                                        onChange={event => setReferenciaPago(event.target.value.slice(0, 40))}
                                        className={inputCls}
                                        maxLength={40}
                                        placeholder={formaPago === 'transferencia' ? 'Ej: TRX000123' : 'Ej: CHQ000123'}
                                      />
                                    </div>
                                  </div>
                                )}

                                {formaPago === 'nota_credito' && (
                                  <div>
                                    <label className={labelCls}>Numero de nota de crédito</label>
                                    <input
                                      value={referenciaPago}
                                      onChange={event => setReferenciaPago(event.target.value.slice(0, 40))}
                                      className={inputCls}
                                      maxLength={40}
                                      placeholder="Ej: NC-000123"
                                    />
                                  </div>
                                )}
                              </fieldset>
                            )}

                            {!isEnvio && (
                              <div>
                                <label className={labelCls}>{hasMultipleGuias ? (isTransferencia ? 'Guías firmadas opcionales' : 'Guías firmadas') : (isTransferencia ? 'Firma opcional de guía PDF' : 'Firma de guía PDF')}</label>
                                {hasMultipleGuias ? (
                                  <div className="space-y-3 rounded-[24px] border border-stone-200 bg-stone-50/70 p-4">
                                    <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                                      <p className="text-sm font-medium text-stone-700">
                                        {devolucionActiva
                                          ? isTransferencia
                                            ? 'Si deseas, puedes firmar las guías y marcar cuáles regresan completas o qué productos se devuelven antes de finalizar la transferencia.'
                                            : 'Revisa las guías firmadas y marca cuáles regresan completas o qué productos se devuelven. Las guías con productos aceptados siguen requiriendo firma.'
                                          : isTransferencia
                                            ? 'Puedes firmar las guías si lo necesitas, pero la transferencia puede finalizarse sin firma.'
                                            : 'Revisa que todas las guías estén firmadas antes de finalizar la entrega.'}
                                      </p>
                                      <span className="text-xs font-semibold text-stone-500">
                                        {guiasFirmadasViaje.filter(guia => guia.url).length}/{guiasPdfViaje.length} firmadas
                                      </span>
                                    </div>
                                    <div className="grid gap-3 lg:grid-cols-2">
                                      {guiasPdfViaje.map((guia, index) => {
                                        const guiaFirmada = guiasFirmadasViaje[index];
                                        const guiaMarcadaComoDevuelta = guiasDevueltasIndexSet.has(index);
                                        const selectedItemKeys = new Set(itemsDevolucionSeleccionados[index] ?? []);
                                        const valorDevueltoGuia = getGuideReturnedAmount(guia, index, guiasDevueltasIndexSet, itemsDevolucionSeleccionados);
                                        return (
                                          <div key={`${guia.url}-signed-${index}`} className="rounded-2xl bg-white px-4 py-3 text-sm text-stone-600">
                                            <div className="flex items-start justify-between gap-3">
                                              <div>
                                                <p className="font-semibold text-stone-900">Guía {index + 1}</p>
                                                <p className="mt-1 text-xs text-stone-500">Factura: {guia.numeroFactura || '—'}</p>
                                                <p className="mt-1 text-xs text-stone-500">Dirección: {guia.direccion || '—'}</p>
                                                <p className="mt-1 text-xs text-stone-500">Valor: {formatCurrency(guia.valorTotal)}</p>
                                                  {devolucionActiva && valorDevueltoGuia > 0 && (
                                                  <p className="mt-1 text-xs text-rose-600">Devuelto: {formatCurrency(valorDevueltoGuia)}</p>
                                                )}
                                              </div>
                                              <div className="flex flex-wrap justify-end gap-2">
                                                <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${guiaFirmada?.url ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
                                                  {guiaFirmada?.url ? 'Firmada' : 'Pendiente'}
                                                </span>
                                                  {devolucionActiva && guiaMarcadaComoDevuelta && (
                                                  <span className="rounded-full bg-rose-50 px-2.5 py-1 text-[11px] font-semibold text-rose-700">
                                                    Devuelta
                                                  </span>
                                                )}
                                              </div>
                                            </div>
                                            <div className="mt-3 flex flex-wrap gap-2">
                                              <a href={guia.url} target="_blank" rel="noreferrer" className="rounded-full bg-stone-100 px-3 py-1.5 font-medium text-stone-700 transition hover:bg-stone-200">
                                                Ver guía
                                              </a>
                                              {guiaFirmada?.url ? (
                                                <a href={guiaFirmada.url} target="_blank" rel="noreferrer" className="rounded-full bg-emerald-50 px-3 py-1.5 font-medium text-emerald-700 transition hover:bg-emerald-100">
                                                  Ver firmada
                                                </a>
                                              ) : guiaMarcadaComoDevuelta ? (
                                                <span className="rounded-full bg-rose-50 px-3 py-1.5 font-medium text-rose-700">
                                                  No requiere firma
                                                </span>
                                              ) : (
                                                <button
                                                  type="button"
                                                  onClick={() => abrirFirmaGuia(index)}
                                                  disabled={signingPdf || loading}
                                                  className="rounded-full bg-stone-950 px-3 py-1.5 font-medium text-white transition hover:bg-stone-700 disabled:opacity-50"
                                                >
                                                  {isTransferencia ? 'Firmar opcional' : 'Firmar pendiente'}
                                                </button>
                                              )}
                                            </div>
                                            {viaje?.tipoEntrega === 'entrega' && (
                                              <GuiaItemsDetallePanel
                                                guia={guia}
                                                guiaIndex={index}
                                                guiaFirmadaUrl={guiaFirmada?.url}
                                                devolucionActiva={devolucionActiva}
                                                guiaMarcadaComoDevuelta={guiaMarcadaComoDevuelta}
                                                selectedItemKeys={selectedItemKeys}
                                                onToggleGuiaDevolucion={() => toggleGuiaDevolucion(index)}
                                                onToggleItemDevolucion={itemKey => toggleGuiaItemDevolucion(index, itemKey)}
                                              />
                                            )}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                ) : guiasPdfViaje.length > 0 ? (
                                  <div className="space-y-4 rounded-[24px] border border-stone-200 bg-stone-50/70 p-4">
                                    {(() => {
                                      const guia = guiasPdfViaje[0]!;
                                      const guiaFirmada = guiasFirmadasViaje[0];
                                      const guiaMarcadaComoDevuelta = guiasDevueltasIndexSet.has(0);
                                      const selectedItemKeys = new Set(itemsDevolucionSeleccionados[0] ?? []);
                                      const valorDevueltoGuia = getGuideReturnedAmount(guia, 0, guiasDevueltasIndexSet, itemsDevolucionSeleccionados);

                                      return (
                                        <div className="space-y-3">
                                          <div className="flex items-start justify-between gap-3">
                                            <div>
                                              <p className="text-sm font-semibold text-stone-900">Guía principal</p>
                                              <p className="mt-1 text-xs text-stone-500">Factura: {guia.numeroFactura || '—'}</p>
                                              <p className="mt-1 text-xs text-stone-500">Dirección: {guia.direccion || '—'}</p>
                                              <p className="mt-1 text-xs text-stone-500">Valor: {formatCurrency(guia.valorTotal)}</p>
                                                {devolucionActiva && valorDevueltoGuia > 0 && (
                                                <p className="mt-1 text-xs text-rose-600">Devuelto: {formatCurrency(valorDevueltoGuia)}</p>
                                              )}
                                            </div>
                                            <div className="flex flex-wrap justify-end gap-2">
                                              <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${guiaFirmada?.url ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
                                                {guiaFirmada?.url ? 'Firmada' : 'Pendiente'}
                                              </span>
                                                {devolucionActiva && guiaMarcadaComoDevuelta && (
                                                <span className="rounded-full bg-rose-50 px-2.5 py-1 text-[11px] font-semibold text-rose-700">
                                                  Devuelta
                                                </span>
                                              )}
                                            </div>
                                          </div>

                                          <p className="text-sm font-medium text-stone-700">
                                            {devolucionActiva
                                              ? isTransferencia
                                                ? 'Puedes devolver toda la guía o seleccionar productos específicos. La firma sigue disponible, pero es opcional para esta transferencia.'
                                                : 'Puedes devolver toda la guía o seleccionar productos específicos. Si solo devuelves algunos productos, la guía firmada sigue siendo obligatoria.'
                                              : isTransferencia
                                                ? 'Si lo necesitas, abre la guía y dibuja la firma directamente sobre el PDF antes de guardarla. También puedes cerrar la transferencia sin firmar.'
                                                : 'Abre la guía y dibuja la firma directamente sobre el PDF antes de guardarla.'}
                                          </p>

                                          <div className="flex flex-wrap items-center gap-3">
                                            <a href={guia.url} target="_blank" rel="noreferrer" className="rounded-2xl bg-stone-100 px-4 py-2 text-sm font-medium text-stone-700 transition hover:bg-stone-200">
                                              Ver guía
                                            </a>
                                            {guiaFirmada?.url ? (
                                              <a href={guiaFirmada.url} target="_blank" rel="noreferrer" className="rounded-2xl bg-emerald-50 px-4 py-2 text-sm font-medium text-emerald-700 transition hover:bg-emerald-100">
                                                Ver guía firmada
                                              </a>
                                            ) : guiaMarcadaComoDevuelta ? (
                                              <span className="rounded-2xl bg-rose-50 px-4 py-2 text-sm font-medium text-rose-700">
                                                No requiere firma
                                              </span>
                                            ) : (
                                              <button
                                                type="button"
                                                onClick={() => abrirFirmaGuia(0)}
                                                disabled={signingPdf || loading}
                                                className="rounded-2xl bg-stone-950 px-4 py-2 text-sm font-semibold text-white transition hover:bg-stone-700 disabled:opacity-50"
                                              >
                                                {signingPdf ? 'Firmando guía...' : isTransferencia ? 'Abrir guía para firmar opcionalmente' : 'Abrir guía para firmar'}
                                              </button>
                                            )}
                                          </div>

                                          {signedPdfName && (
                                            <p className="text-sm text-stone-600">Archivo firmado: {signedPdfName}</p>
                                          )}

                                          {viaje?.tipoEntrega === 'entrega' && (
                                            <GuiaItemsDetallePanel
                                              guia={guia}
                                              guiaIndex={0}
                                              guiaFirmadaUrl={guiaFirmada?.url}
                                              devolucionActiva={devolucionActiva}
                                              guiaMarcadaComoDevuelta={guiaMarcadaComoDevuelta}
                                              selectedItemKeys={selectedItemKeys}
                                              onToggleGuiaDevolucion={() => toggleGuiaDevolucion(0)}
                                              onToggleItemDevolucion={itemKey => toggleGuiaItemDevolucion(0, itemKey)}
                                            />
                                          )}
                                        </div>
                                      );
                                    })()}
                                  </div>
                                ) : (
                                  <p className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
                                    {isTransferencia
                                      ? 'Esta transferencia no tiene una guía PDF cargada para firmar. Puedes finalizarla sin firma.'
                                      : 'Esta entrega no tiene una guía PDF cargada para firmar.'}
                                  </p>
                                )}
                              </div>
                            )}

                            <div className="rounded-[24px] border border-stone-200 bg-stone-50/70 p-4">
                              <label className={labelCls}>
                                {requiereEvidenciaEntrega ? 'Imágenes de entrega' : 'Imágenes de entrega (opcional)'}
                              </label>
                              {preferCameraCapture ? (
                                <div className="space-y-3">
                                  <div className="grid gap-3 sm:grid-cols-2">
                                    <button
                                      type="button"
                                      onClick={() => void openCameraCapture('entrega')}
                                      disabled={uploadingImg}
                                      className="w-full rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-700 transition hover:bg-emerald-100 disabled:opacity-60"
                                    >
                                      Abrir cámara del dispositivo
                                    </button>
                                    <label className={`flex w-full cursor-pointer items-center justify-center rounded-2xl border border-stone-200 bg-white px-4 py-3 text-sm font-semibold text-stone-700 transition hover:bg-stone-50 ${uploadingImg ? 'pointer-events-none opacity-60' : ''}`}>
                                      Seleccionar archivos
                                      <input
                                        type="file"
                                        accept="image/*"
                                        multiple
                                        onChange={handleImagenEntrega}
                                        disabled={uploadingImg}
                                        className="hidden"
                                      />
                                    </label>
                                  </div>
                                  <p className="text-xs text-stone-400">
                                    {requiereEvidenciaEntrega
                                      ? 'Debes registrar al menos una evidencia usando la cámara o seleccionando un archivo desde tu dispositivo. Ambas opciones se suben a MinIO.'
                                      : 'Puedes tomar una foto en el popup o seleccionar imágenes desde tu dispositivo. Ambas opciones se suben a MinIO.'}
                                  </p>
                                </div>
                              ) : (
                                <input
                                  type="file"
                                  accept="image/*"
                                  multiple
                                  onChange={handleImagenEntrega}
                                  className="block w-full text-sm text-stone-500 file:mr-3 file:rounded-2xl file:border-0 file:bg-emerald-50 file:px-3 file:py-2 file:text-emerald-700 hover:file:bg-emerald-100"
                                />
                              )}
                              {uploadingImg && <p className="mt-2 text-xs text-stone-400">Subiendo imágenes de entrega...</p>}
                              {preferCameraCapture && cameraError && (!cameraTarget || cameraTarget === 'entrega') && <p className="mt-2 text-xs text-red-500">{cameraError}</p>}
                              {imagenesEntrega.length > 0 && (
                                <div className="mt-3 flex flex-wrap gap-2">
                                  {imagenesEntrega.map((imagenUrl, index) => (
                                    <span key={`${imagenUrl}-${index}`} className="inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1.5 text-sm font-medium text-emerald-700">
                                      <a href={imagenUrl} target="_blank" rel="noreferrer" className="underline">
                                        Imagen {index + 1}
                                      </a>
                                      <button
                                        type="button"
                                        onClick={() => removeImagenEntrega(index)}
                                        className="text-emerald-900 transition hover:text-red-700"
                                        aria-label={`Quitar imagen ${index + 1}`}
                                      >
                                        x
                                      </button>
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>

                            <div className="rounded-[24px] border border-stone-200 bg-stone-50/70 p-4">
                              <label className={labelCls}>Observaciones</label>
                              <textarea
                                value={observaciones}
                                onChange={event => setObservaciones(event.target.value)}
                                rows={4}
                                className={inputCls}
                                required={isTransferencia}
                                placeholder="Incidencias, novedades o comentario final"
                              />
                            </div>

                            {error && (
                              <p className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                                {error}
                              </p>
                            )}

                            <button
                              type="submit"
                              disabled={loading || uploadingImg || signingPdf}
                              className="w-full rounded-2xl bg-stone-950 py-3 text-sm font-semibold text-white transition hover:bg-stone-700 disabled:opacity-50"
                            >
                              {loading ? 'Finalizando...' : 'Finalizar entrega'}
                            </button>
                          </form>
                        </div>
                      )}
                    </>
                  )}
                </section>
              )}

              {section === 'asignadas' && (
                <section className="space-y-4">
                  <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_220px]">
                    <div className="rounded-[24px] border border-white/80 bg-white/85 px-5 py-4 shadow-[0_20px_50px_rgba(120,113,108,0.08)]">
                      <p className="text-sm font-semibold text-stone-900">Entregas asignadas</p>
                      <p className="mt-1 text-sm text-stone-500">
                        Las entregas se mantienen en Pendiente hasta que inicies el viaje. Solo se cargan a Viaje actual cuando las abras desde esta vista.
                      </p>
                    </div>
                    <div>
                      <label className={labelCls}>Filtrar por estado</label>
                      <select
                        value={asignadasEstadoFiltro}
                        onChange={event => setAsignadasEstadoFiltro(event.target.value as AsignadasEstadoFiltro)}
                        className={inputCls}
                      >
                        <option value="todas">Todas</option>
                        <option value="pendiente">Pendientes</option>
                        <option value="en_curso">En camino</option>
                      </select>
                    </div>
                  </div>

                  {viajesActivos.length === 0 ? (
                    <div className="rounded-[30px] border border-dashed border-stone-300 bg-white/70 p-12 text-center text-stone-500">
                      No tienes entregas activas ni pendientes.
                    </div>
                  ) : viajesAsignadosFiltrados.length === 0 ? (
                    <div className="rounded-[30px] border border-dashed border-stone-300 bg-white/70 p-12 text-center text-stone-500">
                      No hay entregas para el filtro seleccionado.
                    </div>
                  ) : (
                    <div className="overflow-hidden rounded-[28px] border border-white/80 bg-white/85 shadow-[0_20px_50px_rgba(120,113,108,0.1)]">
                      <div className="flex flex-wrap items-center gap-3 border-b border-stone-200 px-5 py-4 text-sm text-stone-600">
                        <p className="font-semibold text-stone-900">Leyenda de tipos</p>
                        {(['entrega', 'envio', 'transferencia'] as Array<Viaje['tipoEntrega']>).map(tipo => {
                          const tone = getAssignedTripTone(tipo);
                          return (
                            <span key={tipo} className="inline-flex items-center gap-2 rounded-full bg-stone-100/80 px-3 py-1.5">
                              <span className={`h-3 w-3 rounded-full ${tone.dot}`} />
                              <span>{formatDeliveryTypeLabel(tipo)}</span>
                            </span>
                          );
                        })}
                      </div>

                      <div className="overflow-x-auto">
                        <table className="block w-full text-left text-sm text-stone-700 lg:table lg:min-w-[1080px]">
                          <thead className="hidden bg-stone-50 text-xs uppercase tracking-[0.16em] text-stone-500 lg:table-header-group">
                            <tr>
                              <th className="px-5 py-3 font-semibold">Nombre de la entrega</th>
                              <th className="px-5 py-3 font-semibold">Guías</th>
                              <th className="px-5 py-3 font-semibold">Destino</th>
                              <th className="px-5 py-3 font-semibold">Detalles</th>
                            </tr>
                          </thead>
                          <tbody className="block divide-y divide-stone-200 lg:table-row-group lg:divide-white/80">
                            {viajesAsignadosFiltrados.map(item => {
                              const tone = getAssignedTripTone(item.tipoEntrega);
                              const guias = getGuiasPdfViaje(item);
                              const vendedorNombre = item.vendedor?.nombre || item.vendedor?.username || 'Sin vendedor';

                              return (
                                <tr key={item.id} className={`${tone.row} block align-top lg:table-row`}>
                                  <td className="block px-5 py-2 first:pt-4 last:pb-4 lg:table-cell lg:py-4">
                                    <div className="space-y-3">
                                      <div className="flex flex-wrap items-center gap-2">
                                        <span className={`rounded-full px-3 py-1 text-xs font-semibold ${tone.chip}`}>
                                          {formatDeliveryTypeLabel(item.tipoEntrega)}
                                        </span>
                                        <span className={`rounded-full px-3 py-1 text-xs font-semibold ${estadoBadge(item)}`}>
                                          {estadoLabel(item)}
                                        </span>
                                      </div>
                                      <div>
                                        <p className="text-base font-semibold text-stone-950">{item.paquete?.nombre || 'Paquete sin nombre'}</p>
                                      </div>
                                    </div>
                                  </td>
                                  <td className="block px-5 py-2 first:pt-4 last:pb-4 lg:table-cell lg:py-4">
                                    {guias.length > 0 ? (
                                      <div className="flex flex-col gap-2 break-all lg:min-w-[220px]">
                                        {guias.map((guia, index) => (
                                          <a
                                            key={`${item.id}-guia-${index}`}
                                            href={guia.url}
                                            target="_blank"
                                            rel="noreferrer"
                                            className={`font-medium underline underline-offset-2 transition ${tone.link}`}
                                          >
                                            {guia.nombreArchivo || `Guía ${index + 1}`}
                                          </a>
                                        ))}
                                      </div>
                                    ) : (
                                      <span className="text-stone-400">Sin guías</span>
                                    )}
                                  </td>
                                  <td className="block px-5 py-2 first:pt-4 last:pb-4 lg:table-cell lg:py-4">
                                    {item.urlDestino ? (
                                      <a
                                        href={item.urlDestino}
                                        target="_blank"
                                        rel="noreferrer"
                                        className={`font-medium underline underline-offset-2 transition ${tone.link}`}
                                      >
                                        Abrir destino en Google Maps
                                      </a>
                                    ) : (
                                      <span className="text-stone-400">Sin destino</span>
                                    )}
                                  </td>
                                  <td className="block px-5 py-2 first:pt-4 last:pb-4 lg:table-cell lg:py-4">
                                    <div className="space-y-3">
                                      <div className="space-y-2 text-sm text-stone-700">
                                        <div>
                                          <span className="font-semibold text-stone-900">Descripción:</span>
                                          <DescripcionResumida viaje={item} texto={formatDescripcionPorFactura(item.paquete?.descripcion)} className="mt-0.5" />
                                        </div>
                                        {item.tipoEntrega === 'entrega' && (
                                          <p>
                                            <span className="font-semibold text-stone-900">Vendedor/a:</span>{' '}
                                            {vendedorNombre}
                                          </p>
                                        )}
                                        {item.tipoEntrega !== 'transferencia' && (
                                          <p>
                                            <span className="font-semibold text-stone-900">Cliente:</span>{' '}
                                            {item.paquete?.cliente?.nombre || 'Sin cliente'}
                                          </p>
                                        )}
                                        {item.tipoEntrega !== 'transferencia' && (
                                          <p>
                                            <span className="font-semibold text-stone-900">Correo:</span>{' '}
                                            {item.paquete?.cliente?.correoElectronico || 'Sin correo'}
                                          </p>
                                        )}
                                        <p>
                                          <span className="font-semibold text-stone-900">Valor total factura:</span>{' '}
                                          {formatCurrency(getInvoiceTotal(item.paquete))}
                                        </p>
                                        {item.tipoEntrega !== 'transferencia' && (
                                          <p>
                                            <span className="font-semibold text-stone-900">Estado:</span>{' '}
                                            {item.paquete?.pago?.tipo === 'completo'
                                              ? (item.paquete?.pago?.medioPago ? `Pagado (${formatPaymentLabel(item.paquete.pago.medioPago)})` : 'Pagado')
                                              : item.paquete?.pago?.tipo === 'parcial'
                                                ? `Por cobrar el saldo (${formatPaymentLabel(item.paquete?.pago?.tipoPagoParcial)})`
                                                : `Por cobrar (${formatPaymentLabel(item.paquete?.pago?.tipo)})`}
                                          </p>
                                        )}
                                        {(() => {
                                          const telefonoCliente = item.paquete?.cliente?.telefono?.trim() || '';
                                          const whatsappPhone = getWhatsAppPhone(telefonoCliente);
                                          const dialablePhone = getDialablePhone(telefonoCliente);
                                          if (!whatsappPhone && !dialablePhone) return null;
                                          return (
                                            <p className="flex flex-wrap items-center gap-2">
                                              <span className="font-semibold text-stone-900">Teléfono:</span>
                                              <span>{telefonoCliente || '—'}</span>
                                              {whatsappPhone && (
                                                <a
                                                  href={`https://wa.me/${whatsappPhone}`}
                                                  target="_blank"
                                                  rel="noreferrer"
                                                  aria-label="Abrir WhatsApp del cliente"
                                                  title="Abrir WhatsApp del cliente"
                                                  className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-emerald-200 bg-emerald-50 text-emerald-700 transition hover:bg-emerald-100"
                                                >
                                                  <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 fill-current" aria-hidden="true">
                                                    <path d="M19.05 4.91A9.82 9.82 0 0 0 12.03 2C6.62 2 2.21 6.4 2.21 11.82c0 1.73.45 3.42 1.31 4.9L2 22l5.44-1.43a9.8 9.8 0 0 0 4.58 1.16h.01c5.41 0 9.82-4.41 9.82-9.82a9.75 9.75 0 0 0-2.8-7ZM12.03 20.07h-.01a8.1 8.1 0 0 1-4.13-1.13l-.3-.18-3.23.85.86-3.15-.2-.32a8.12 8.12 0 0 1-1.25-4.32c0-4.5 3.67-8.17 8.18-8.17 2.18 0 4.22.85 5.77 2.39a8.08 8.08 0 0 1 2.39 5.78c0 4.5-3.67 8.17-8.18 8.17Zm4.48-6.13c-.24-.12-1.41-.69-1.63-.76-.22-.08-.38-.12-.54.12-.16.24-.62.76-.76.92-.14.16-.28.18-.52.06-.24-.12-1-.37-1.91-1.18-.71-.63-1.19-1.41-1.33-1.65-.14-.24-.01-.37.11-.49.11-.11.24-.28.36-.42.12-.14.16-.24.24-.4.08-.16.04-.3-.02-.42-.06-.12-.54-1.29-.74-1.77-.2-.47-.4-.41-.54-.42h-.46c-.16 0-.42.06-.64.3-.22.24-.84.82-.84 2s.86 2.31.98 2.47c.12.16 1.69 2.58 4.11 3.62.57.25 1.02.4 1.37.51.58.18 1.11.15 1.52.09.46-.07 1.41-.58 1.61-1.14.2-.56.2-1.04.14-1.14-.06-.1-.22-.16-.46-.28Z" />
                                                  </svg>
                                                </a>
                                              )}
                                              {dialablePhone && (
                                                <button
                                                  type="button"
                                                  onClick={() => {
                                                    void openPhoneDialer(telefonoCliente);
                                                  }}
                                                  aria-label="Abrir aplicación de teléfono y copiar número"
                                                  title="Abrir aplicación de teléfono y copiar número"
                                                  className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-sky-200 bg-sky-50 text-sky-700 transition hover:bg-sky-100"
                                                >
                                                  <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 stroke-current" aria-hidden="true" fill="none" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.12 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.12.89.33 1.76.63 2.6a2 2 0 0 1-.45 2.11L8.02 9.7a16 16 0 0 0 6.28 6.28l1.27-1.27a2 2 0 0 1 2.11-.45c.84.3 1.71.51 2.6.63A2 2 0 0 1 22 16.92Z" />
                                                  </svg>
                                                </button>
                                              )}
                                            </p>
                                          );
                                        })()}
                                        {item.tipoEntrega === 'envio' && (
                                          <p>
                                            <span className="font-semibold text-stone-900">Compañía:</span>{' '}
                                            {item.companiaTransporte || '—'}
                                          </p>
                                        )}
                                      </div>

                                      <div>
                                        <button
                                          type="button"
                                          onClick={() => handleAbrirEntregaAsignada(item)}
                                          className="rounded-2xl bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-emerald-700"
                                        >
                                          {item.estado === 'en_curso' ? 'Abrir viaje en curso' : 'Abrir entrega asignada'}
                                        </button>
                                      </div>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </section>
              )}

              {section === 'gasolina' && (
                <section className="space-y-6">
                  {conductorVehiculos.length === 0 ? (
                    <div className="rounded-[30px] border border-dashed border-stone-300 bg-white/70 p-12 text-center text-stone-500">
                      No tienes vehículos asignados. Solicita al administrador que te asigne al menos uno para registrar cargas de gasolina.
                    </div>
                  ) : (
                    <>
                      <div className="grid gap-4 xl:grid-cols-[0.95fr_1.05fr]">
                        <SectionSurface>
                          <div>
                            <h3 className="text-lg font-semibold text-stone-900">Registrar carga</h3>
                            <p className="mt-1 text-sm text-stone-500">
                              Guarda cada carga con vehículo, voucher, galones, total y forma de pago. Los campos con <span className="font-semibold text-red-600">*</span> son obligatorios.
                            </p>
                          </div>

                          <form onSubmit={handleGuardarCargaGasolina} className="mt-5 space-y-4">
                            <div>
                              <label className={labelCls}>Vehículo</label>
                              <select
                                value={cargaGasolinaVehiculoId}
                                onChange={event => setCargaGasolinaVehiculoId(event.target.value)}
                                className={inputCls}
                              >
                                {conductorVehiculos.map(vehiculo => (
                                  <option key={vehiculo.id} value={vehiculo.id}>
                                    {vehiculo.placa} · {vehiculo.marca} {vehiculo.modelo}
                                  </option>
                                ))}
                              </select>
                            </div>

                            <div>
                              <label className={labelCls}>Forma de pago</label>
                              <select
                                value={cargaGasolinaFormaPago}
                                onChange={event => {
                                  const nextFormaPago = event.target.value as FormaPagoCargaGasolina;
                                  setCargaGasolinaFormaPago(nextFormaPago);
                                  setCargaGasolinaUltimosCuatro('');
                                }}
                                className={inputCls}
                              >
                                <option value="efectivo">Efectivo</option>
                                <option value="tc">TC (tarjeta de crédito)</option>
                              </select>
                            </div>

                            {cargaGasolinaFormaPago === 'tc' && (
                              <div>
                                <label className={labelCls}>Últimos 4 dígitos de la tarjeta <span className="text-red-600">*</span></label>
                                <input
                                  value={cargaGasolinaUltimosCuatro}
                                  onChange={event => setCargaGasolinaUltimosCuatro(event.target.value.replace(/\D/g, '').slice(0, 4))}
                                  className={inputCls}
                                  inputMode="numeric"
                                  maxLength={4}
                                  placeholder="1234"
                                />
                              </div>
                            )}

                            <div>
                              <label className={labelCls}>
                                Número de factura{cargaGasolinaFormaPago === 'tc' ? (<span className="text-red-600"> *</span>) : (<span className="text-stone-400 font-normal normal-case tracking-normal text-[11px]"> (opcional)</span>)}
                              </label>
                              <input
                                value={cargaGasolinaNumeroVoucher}
                                onChange={event => setCargaGasolinaNumeroVoucher(event.target.value.slice(0, 120))}
                                className={inputCls}
                                maxLength={120}
                                placeholder="Ej: VC-20260505"
                              />
                            </div>

                            <div>
                              <label className={labelCls}>Foto del voucher o factura <span className="text-red-600">*</span></label>
                              {preferCameraCapture ? (
                                <div className="space-y-3">
                                  <button
                                    type="button"
                                    onClick={() => void openCameraCapture('gasolina')}
                                    disabled={uploadingCargaGasolinaImg}
                                    className="w-full rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-700 transition hover:bg-emerald-100 disabled:opacity-60"
                                  >
                                    {cargaGasolinaImgFacturavou ? 'Tomar nueva foto' : 'Abrir cámara del dispositivo'}
                                  </button>
                                  <p className="text-xs text-stone-400">Solo se guarda una foto. Si capturas otra, reemplazará la actual.</p>
                                </div>
                              ) : (
                                <div className="space-y-3">
                                  <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleCargaGasolinaVoucherImage}
                                    className="block w-full text-sm text-stone-500 file:mr-3 file:rounded-2xl file:border-0 file:bg-emerald-50 file:px-3 file:py-2 file:text-emerald-700 hover:file:bg-emerald-100"
                                  />
                                  <p className="text-xs text-stone-400">Solo se guarda una foto. Si seleccionas otra, reemplazará la actual.</p>
                                </div>
                              )}
                              {uploadingCargaGasolinaImg && <p className="mt-2 text-xs text-stone-400">Subiendo foto del voucher o factura...</p>}
                              {preferCameraCapture && cameraError && (!cameraTarget || cameraTarget === 'gasolina') && <p className="mt-2 text-xs text-red-500">{cameraError}</p>}
                              {cargaGasolinaImgFacturavou && (
                                <div className="mt-3 overflow-hidden rounded-[24px] border border-stone-200 bg-stone-50/80">
                                  <img src={cargaGasolinaImgFacturavou} alt="Voucher o factura" className="h-48 w-full object-cover" />
                                  <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
                                    <a
                                      href={cargaGasolinaImgFacturavou}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="text-sm font-semibold text-emerald-700 underline"
                                    >
                                      Abrir foto actual
                                    </a>
                                    <button
                                      type="button"
                                      onClick={removeCargaGasolinaVoucherImage}
                                      className="rounded-full border border-red-200 px-3 py-1.5 text-sm font-semibold text-red-700 transition hover:bg-red-50"
                                    >
                                      Eliminar foto
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>

                            <div className="grid gap-4 md:grid-cols-2">
                              <div>
                                <label className={labelCls}>Cantidad en galones <span className="text-red-600">*</span></label>
                                <input
                                  type="number"
                                  min={0.01}
                                  step="0.01"
                                  value={cargaGasolinaCantidadGalones}
                                  onWheel={preventNumberInputWheelChange}
                                  onChange={event => setCargaGasolinaCantidadGalones(event.target.value)}
                                  className={inputCls}
                                  placeholder="Ej: 10.5"
                                />
                              </div>
                              <div>
                                <label className={labelCls}>Total pagado <span className="text-red-600">*</span></label>
                                <input
                                  type="number"
                                  min={0.01}
                                  step="0.01"
                                  value={cargaGasolinaTotalPagado}
                                  onWheel={preventNumberInputWheelChange}
                                  onChange={event => setCargaGasolinaTotalPagado(event.target.value)}
                                  className={inputCls}
                                  placeholder="Ej: 31.80"
                                />
                              </div>
                            </div>

                            {cargaGasolinaMensaje && (
                              <p className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
                                {cargaGasolinaMensaje}
                              </p>
                            )}

                            {cargaGasolinaError && (
                              <p className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                                {cargaGasolinaError}
                              </p>
                            )}

                            <button
                              type="submit"
                              disabled={cargaGasolinaSaving || uploadingCargaGasolinaImg}
                              className="w-full rounded-2xl bg-stone-950 py-3 text-sm font-semibold text-white transition hover:bg-stone-700 disabled:opacity-50"
                            >
                              {uploadingCargaGasolinaImg ? 'Subiendo foto...' : cargaGasolinaSaving ? 'Guardando...' : 'Guardar carga de gasolina'}
                            </button>
                          </form>
                        </SectionSurface>

                        <div className="overflow-hidden rounded-[28px] border border-white/80 bg-white/90 shadow-[0_20px_50px_rgba(120,113,108,0.1)]">
                          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-stone-200 px-5 py-4">
                            <div>
                              <h3 className="text-lg font-semibold text-stone-900">Historial reciente</h3>
                              <p className="text-sm text-stone-500">Tus cargas se muestran de 10 en 10 y se ordenan desde la más nueva.</p>
                            </div>
                            <p className="text-sm text-stone-500">{cargasGasolinaTotalRegistros} registros</p>
                          </div>

                          {cargasGasolinaCargando ? (
                            <div className="border-b border-stone-200 bg-blue-50 px-5 py-3 text-sm text-blue-700">Actualizando cargas de gasolina...</div>
                          ) : null}

                          {cargasGasolina.length === 0 && !cargasGasolinaCargando ? (
                            <div className="px-5 py-12 text-center text-stone-500">Aún no has registrado cargas de gasolina.</div>
                          ) : (
                            <>
                              <div className="overflow-x-auto">
                                <table className="tabla-apilada min-w-full text-left text-sm text-stone-600">
                                  <thead className="bg-stone-50 text-xs uppercase tracking-[0.16em] text-stone-500">
                                    <tr>
                                      <th className="px-5 py-3 font-semibold">Fecha</th>
                                      <th className="px-5 py-3 font-semibold">Vehículo</th>
                                      <th className="px-5 py-3 font-semibold">Pago</th>
                                      <th className="px-5 py-3 font-semibold">Voucher</th>
                                      <th className="px-5 py-3 font-semibold">Galones</th>
                                      <th className="px-5 py-3 font-semibold">Total</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {cargasGasolina.map(carga => (
                                      <tr key={carga.id} className="border-t border-stone-100 align-top">
                                        <td data-label="Fecha" className="px-5 py-4">{formatDateTime(carga.fechaRegistro)}</td>
                                        <td data-label="Vehículo" className="px-5 py-4 font-semibold text-stone-900">{carga.vehiculo?.placa || '—'}</td>
                                        <td data-label="Pago" className="px-5 py-4">
                                          {formatPaymentLabel(carga.formaPago)}
                                          {carga.formaPago === 'tc' && carga.ultimosCuatroTarjeta ? ` · ****${carga.ultimosCuatroTarjeta}` : ''}
                                        </td>
                                        <td data-label="Voucher" className="px-5 py-4">{carga.numeroVoucher}</td>
                                        <td data-label="Galones" className="px-5 py-4">{carga.cantidadGalones.toFixed(2)}</td>
                                        <td data-label="Total" className="px-5 py-4">{formatCurrency(carga.totalPagado)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>

                              <div className="flex flex-wrap items-center justify-between gap-3 border-t border-stone-200 px-5 py-4 text-sm text-stone-500">
                                <p>
                                  Mostrando {Math.min((cargasGasolinaPaginaActual - 1) * HISTORIAL_PAGE_SIZE + 1, cargasGasolinaTotalRegistros)}-
                                  {Math.min(cargasGasolinaPaginaActual * HISTORIAL_PAGE_SIZE, cargasGasolinaTotalRegistros)} de {cargasGasolinaTotalRegistros}
                                </p>
                                <div className="flex items-center gap-2">
                                  <button
                                    type="button"
                                    onClick={() => setCargasGasolinaPagina(current => Math.max(1, current - 1))}
                                    disabled={cargasGasolinaPaginaActual === 1}
                                    className="rounded-2xl border border-stone-200 px-3 py-2 font-medium text-stone-700 transition hover:bg-stone-100 disabled:opacity-50"
                                  >
                                    Anterior
                                  </button>
                                  <span>Página {cargasGasolinaPaginaActual} de {cargasGasolinaTotalPaginas}</span>
                                  <button
                                    type="button"
                                    onClick={() => setCargasGasolinaPagina(current => Math.min(cargasGasolinaTotalPaginas, current + 1))}
                                    disabled={cargasGasolinaPaginaActual === cargasGasolinaTotalPaginas}
                                    className="rounded-2xl border border-stone-200 px-3 py-2 font-medium text-stone-700 transition hover:bg-stone-100 disabled:opacity-50"
                                  >
                                    Siguiente
                                  </button>
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </section>
              )}

              {section === 'historial' && (
                <section className="space-y-4">
                  {historialTotalRegistros === 0 && !historialCargando ? (
                    <div className="rounded-[30px] border border-dashed border-stone-300 bg-white/70 p-12 text-center text-stone-500">
                      Aun no tienes viajes finalizados.
                    </div>
                  ) : (
                    <div className="overflow-hidden rounded-[28px] border border-white/80 bg-white/90 shadow-[0_20px_50px_rgba(120,113,108,0.1)]">
                      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-stone-200 px-5 py-4">
                        <div>
                          <h3 className="text-lg font-semibold text-stone-900">Historial de entregas</h3>
                          <p className="text-sm text-stone-500">Se muestran 10 registros por página para mantener la vista ligera.</p>
                        </div>
                        <p className="text-sm text-stone-500">{historialTotalRegistros} registros</p>
                      </div>

                      {historialCargando ? (
                        <div className="border-b border-stone-200 bg-blue-50 px-5 py-3 text-sm text-blue-700">Actualizando historial...</div>
                      ) : null}

                      <div className="ui-filtros border-b border-stone-200 px-5 py-4">
                        <div className="ui-filtro-busqueda">
                          <label className="ui-filtro-label">
                            Buscar historial
                          </label>
                          <input
                            value={historialBusqueda}
                            onChange={event => setHistorialBusqueda(event.target.value)}
                            className="w-full rounded-2xl border border-stone-200 px-3 py-2.5 text-sm text-stone-700 outline-none transition focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100"
                            placeholder="Nombre, fecha, tipo o estado"
                          />
                        </div>
                        <div>
                          <label className="ui-filtro-label">
                            Tipo de entrega
                          </label>
                          <select
                            value={historialTipoFiltro}
                            onChange={event => setHistorialTipoFiltro(event.target.value as HistorialTipoFiltro)}
                            className="w-full rounded-2xl border border-stone-200 px-3 py-2.5 text-sm text-stone-700 outline-none transition focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100"
                          >
                            <option value="todas">Todas</option>
                            <option value="entrega">Entrega</option>
                            <option value="transferencia">Transferencia</option>
                            <option value="envio">Envío</option>
                          </select>
                        </div>
                        <div>
                          <label className="ui-filtro-label">
                            Estado
                          </label>
                          <select
                            value={historialEstadoFiltro}
                            onChange={event => setHistorialEstadoFiltro(event.target.value as HistorialEstadoFiltro)}
                            className="w-full rounded-2xl border border-stone-200 px-3 py-2.5 text-sm text-stone-700 outline-none transition focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100"
                          >
                            <option value="todas">Todos</option>
                            <option value="finalizado">Finalizadas</option>
                            <option value="devolucion">Devoluciones</option>
                          </select>
                        </div>
                      </div>

                      <div className="overflow-x-auto">
                        <table className="tabla-apilada min-w-full text-left text-sm text-stone-600">
                          <thead className="bg-stone-50 text-xs uppercase tracking-[0.16em] text-stone-500">
                            <tr>
                              <th className="px-5 py-3 font-semibold">Entrega</th>
                              <th className="px-5 py-3 font-semibold">Finalización</th>
                              <th className="px-5 py-3 font-semibold">Ubicación destino</th>
                              <th className="px-5 py-3 font-semibold">Dirección</th>
                              <th className="px-5 py-3 font-semibold">Tipo</th>
                              <th className="px-5 py-3 font-semibold">Número de factura</th>
                              <th className="px-5 py-3 font-semibold">Estado</th>
                              <th className="px-5 py-3 font-semibold text-right">Acciones</th>
                            </tr>
                          </thead>
                          <tbody>
                            {historialViajes.map(item => {
                              const invoiceNumbers = getViajeInvoiceNumbers(item);

                              return (
                                <tr key={item.id} className="border-t border-stone-100 align-top">
                                  <td data-label="Entrega" className="px-5 py-4 font-semibold text-stone-900">{item.paquete?.nombre || 'Entrega sin nombre'}</td>
                                  <td data-label="Finalización" className="px-5 py-4">{formatDateTime(item.fechaFin)}</td>
                                  <td data-label="Ubicación destino" className="px-5 py-4">
                                    <div className="max-w-[240px] whitespace-normal break-all leading-5 text-stone-700">
                                      {item.urlDestino ? (
                                        <a
                                          href={item.urlDestino}
                                          target="_blank"
                                          rel="noreferrer"
                                          className="font-medium text-emerald-700 underline decoration-emerald-200 underline-offset-4 transition hover:text-emerald-900"
                                        >
                                          {getHistoryDestinationLabel(item)}
                                        </a>
                                      ) : '—'}
                                    </div>
                                  </td>
                                  <td data-label="Dirección" className="px-5 py-4">{item.direccionEntrega || '—'}</td>
                                  <td data-label="Tipo" className="px-5 py-4">{getHistoryTypeLabel(item)}</td>
                                  <td data-label="Número de factura" className="px-5 py-4">
                                    {invoiceNumbers.length > 0 ? (
                                      <div className="flex max-w-[260px] flex-wrap gap-2">
                                        {invoiceNumbers.map((invoiceNumber, index) => (
                                          <span
                                            key={`${item.id}-historial-invoice-${index}`}
                                            className="rounded-full bg-stone-100 px-3 py-1 text-xs font-medium text-stone-700"
                                          >
                                            {invoiceNumber}
                                          </span>
                                        ))}
                                      </div>
                                    ) : '—'}
                                  </td>
                                  <td data-label="Estado" className="px-5 py-4">
                                    <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${getHistoryStatusBadge(item)}`}>
                                      {getHistoryStatusLabel(item)}
                                    </span>
                                  </td>
                                  <td data-label="" className="px-5 py-4 text-right">
                                    <button
                                      type="button"
                                      onClick={() => setHistorialDetalleViaje(item)}
                                      className="rounded-full bg-emerald-50 px-3 py-1.5 font-medium text-emerald-700 transition hover:bg-emerald-100"
                                    >
                                      Ver detalles
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>

                      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-stone-200 px-5 py-4 text-sm text-stone-500">
                        <p>
                          Mostrando {Math.min((historialPaginaActual - 1) * HISTORIAL_PAGE_SIZE + 1, historialTotalRegistros)}-
                          {Math.min(historialPaginaActual * HISTORIAL_PAGE_SIZE, historialTotalRegistros)} de {historialTotalRegistros}
                        </p>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => setHistorialPagina(current => Math.max(1, current - 1))}
                            disabled={historialPaginaActual === 1}
                            className="rounded-2xl border border-stone-200 px-3 py-2 font-medium text-stone-700 transition hover:bg-stone-100 disabled:opacity-50"
                          >
                            Anterior
                          </button>
                          <span>Página {historialPaginaActual} de {historialTotalPaginas}</span>
                          <button
                            type="button"
                            onClick={() => setHistorialPagina(current => Math.min(historialTotalPaginas, current + 1))}
                            disabled={historialPaginaActual === historialTotalPaginas}
                            className="rounded-2xl border border-stone-200 px-3 py-2 font-medium text-stone-700 transition hover:bg-stone-100 disabled:opacity-50"
                          >
                            Siguiente
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </section>
              )}
            </>
          )}
      </PanelShell>
        {preferCameraCapture && cameraTarget && (
          <div className="fixed inset-0 z-[2190] flex items-center justify-center bg-stone-950/80 px-4 py-6 backdrop-blur-sm">
            <div className="w-full max-w-md rounded-[32px] border border-white/10 bg-stone-950 p-4 shadow-[0_30px_90px_rgba(15,23,42,0.42)]">
              <div className="mb-4 flex items-center justify-between gap-3 text-white">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-300">Cámara</p>
                  <h3 className="mt-1 text-lg font-semibold">
                    {cameraTargetTitle}
                  </h3>
                </div>
                <button
                  type="button"
                  onClick={closeCameraCapture}
                  className="rounded-2xl border border-white/15 px-3 py-2 text-sm font-medium text-white transition hover:bg-white/10"
                >
                  Cerrar
                </button>
              </div>

              <div className="overflow-hidden rounded-[24px] border border-white/10 bg-black">
                <video
                  ref={cameraVideoRef}
                  autoPlay
                  playsInline
                  muted
                  onLoadedMetadata={() => setCameraReady(true)}
                  className="h-[26rem] w-full object-cover"
                />
              </div>

              <div className="mt-4 flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => void handleCapturePhoto()}
                  disabled={cameraTargetUploading || !cameraReady}
                  className="flex-1 rounded-2xl bg-emerald-600 px-4 py-3 text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:opacity-60"
                >
                  {cameraTargetUploading ? 'Subiendo foto...' : 'Tomar y subir foto'}
                </button>
                <button
                  type="button"
                  onClick={closeCameraCapture}
                  className="rounded-2xl border border-white/15 px-4 py-3 text-sm font-semibold text-white transition hover:bg-white/10"
                >
                  Cancelar
                </button>
              </div>

              <p className="mt-3 text-xs text-stone-300">
                La foto capturada en este popup se guarda de inmediato y se carga en el apartado actual.
              </p>
              {cameraError && <p className="mt-2 text-xs text-red-300">{cameraError}</p>}
            </div>
          </div>
        )}
        {signatureModalOpen && !isEnvio && guiasPdfViaje[signatureGuiaIndex ?? 0] && (
          <PdfSignatureModal
            pdfUrl={guiasPdfViaje[signatureGuiaIndex ?? 0]!.url}
            disabled={signingPdf || loading}
            onClose={() => {
              setSignatureModalOpen(false);
              setSignatureGuiaIndex(null);
            }}
            onSave={handleFirmarGuiaPdf}
          />
        )}
          {termsModalOpen && (
            <TermsAcceptanceModal
              accepted={termsAccepted}
              terminosUrl={terminosUrl}
              onAcceptedChange={setTermsAccepted}
              onClose={() => {
                setTermsModalOpen(false);
                setTermsAccepted(false);
                setSignatureGuiaIndex(null);
              }}
              onOpenDocument={() => setTermsPreviewOpen(true)}
              onContinue={() => {
                setTermsModalOpen(false);
                setSignatureModalOpen(true);
              }}
            />
          )}
          {termsPreviewOpen && terminosUrl && (
            <PdfPreviewModal
              title="Términos y condiciones"
              pdfUrl={terminosUrl}
              onClose={() => setTermsPreviewOpen(false)}
            />
          )}
        {historialDetalleViaje && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-stone-950/55 px-4 py-6">
            <div className="max-h-full w-full max-w-4xl overflow-y-auto rounded-[32px] border border-white/70 bg-white shadow-[0_40px_120px_rgba(28,25,23,0.35)]">
              <div className="sticky top-0 flex items-center justify-between border-b border-stone-200 bg-white px-6 py-4">
                <div>
                  <h3 className="text-lg font-semibold text-stone-950">Detalle de la entrega</h3>
                  <p className="text-sm text-stone-500">{historialDetalleViaje.paquete?.nombre || 'Entrega sin nombre'} · {formatDateTime(historialDetalleViaje.fechaFin)}</p>
                </div>
                <button
                  onClick={() => setHistorialDetalleViaje(null)}
                  className="rounded-2xl border border-stone-200 px-3 py-2 text-sm font-medium text-stone-600 transition hover:bg-stone-100"
                >
                  Cerrar
                </button>
              </div>

              <div className="space-y-4 px-6 py-5">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="rounded-[24px] border border-stone-200 bg-stone-50/70 p-4 md:col-span-2">
                    <p className="text-xs font-semibold uppercase tracking-[0.16em] text-stone-500">Descripción de la entrega</p>
                    <DescripcionResumida viaje={historialDetalleViaje} texto={historialDetalleViaje.paquete?.descripcion || '—'} className="mt-2 text-sm text-stone-700" />
                  </div>
                  {historialDetalleViaje.tipoEntrega !== 'transferencia' && (
                    <>
                      <div className="rounded-[24px] border border-stone-200 bg-stone-50/70 p-4">
                        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-stone-500">Cliente</p>
                        <p className="mt-2 text-base font-semibold text-stone-900">{historialDetalleViaje.paquete?.cliente?.nombre || '—'}</p>
                      </div>
                      <div className="rounded-[24px] border border-stone-200 bg-stone-50/70 p-4">
                        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-stone-500">Correo del cliente</p>
                        <p className="mt-2 text-base font-semibold text-stone-900">{historialDetalleViaje.paquete?.cliente?.correoElectronico || '—'}</p>
                      </div>
                      <div className="rounded-[24px] border border-stone-200 bg-stone-50/70 p-4">
                        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-stone-500">Teléfono del cliente</p>
                        <p className="mt-2 text-base font-semibold text-stone-900">{historialDetalleViaje.paquete?.cliente?.telefono || '—'}</p>
                      </div>
                    </>
                  )}
                  <div className="rounded-[24px] border border-stone-200 bg-stone-50/70 p-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.16em] text-stone-500">Valor registrado</p>
                    <p className="mt-2 text-base font-semibold text-stone-900">{formatCurrency(getRecordedAmount(historialDetalleViaje))}</p>
                  </div>
                  <div className="rounded-[24px] border border-stone-200 bg-stone-50/70 p-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.16em] text-stone-500">Estado</p>
                    <p className="mt-2 text-base font-semibold text-stone-900">{getHistoryStatusLabel(historialDetalleViaje)}</p>
                  </div>
                  <div className="rounded-[24px] border border-stone-200 bg-stone-50/70 p-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.16em] text-stone-500">Tipo</p>
                    <p className="mt-2 text-base font-semibold text-stone-900">{getHistoryTypeLabel(historialDetalleViaje)}</p>
                  </div>
                  <div className="rounded-[24px] border border-stone-200 bg-stone-50/70 p-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.16em] text-stone-500">Dirección</p>
                    <p className="mt-2 text-base font-semibold text-stone-900">{historialDetalleViaje.direccionEntrega || '—'}</p>
                  </div>
                  {historialDetalleViaje.tipoEntrega === 'envio' && (
                    <div className="rounded-[24px] border border-stone-200 bg-stone-50/70 p-4">
                      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-stone-500">Compañía de transporte</p>
                      <p className="mt-2 text-base font-semibold text-stone-900">{historialDetalleViaje.companiaTransporte || '—'}</p>
                    </div>
                  )}
                  <div className="rounded-[24px] border border-stone-200 bg-stone-50/70 p-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.16em] text-stone-500">Observaciones</p>
                    <p className="mt-2 text-sm text-stone-700">{historialDetalleViaje.observaciones || '—'}</p>
                  </div>
                </div>

                <div className="flex flex-wrap gap-3 text-sm">
                  {(historialDetalleViaje.imagenesEntrega?.length ? historialDetalleViaje.imagenesEntrega : historialDetalleViaje.imagenEntrega ? [historialDetalleViaje.imagenEntrega] : []).map((imagenUrl, index) => (
                    <a
                      key={`${historialDetalleViaje.id}-delivery-image-${index}`}
                      href={imagenUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="rounded-full bg-blue-50 px-3 py-1.5 font-medium text-blue-700 transition hover:bg-blue-100"
                    >
                      Imagen entrega {index + 1}
                    </a>
                  ))}
                  {getGuiasVisiblesHistorialViaje(historialDetalleViaje).map((guia, index) => (
                    <a
                      key={`${historialDetalleViaje.id}-history-guide-${index}`}
                      href={guia.url}
                      target="_blank"
                      rel="noreferrer"
                      className="rounded-full bg-emerald-50 px-3 py-1.5 font-medium text-emerald-700 transition hover:bg-emerald-100"
                    >
                      {guia.firmada ? `Guía firmada ${index + 1}` : `Guía ${index + 1}`}
                    </a>
                  ))}
                  {getEnvioPdfViaje(historialDetalleViaje) && (
                    <a
                      href={getEnvioPdfViaje(historialDetalleViaje)?.url}
                      target="_blank"
                      rel="noreferrer"
                      className="rounded-full bg-red-50 px-3 py-1.5 font-medium text-red-700 transition hover:bg-red-100"
                    >
                      {getEnvioPdfViaje(historialDetalleViaje)?.nombreArchivo || 'PDF de envío'}
                    </a>
                  )}
                  {historialDetalleViaje.urlDestino && (
                    <a
                      href={historialDetalleViaje.urlDestino}
                      target="_blank"
                      rel="noreferrer"
                      className="rounded-full bg-stone-100 px-3 py-1.5 font-medium text-stone-700 transition hover:bg-stone-200"
                    >
                      Abrir destino
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
    </>
  );
}

function TermsAcceptanceModal({
  accepted,
  terminosUrl,
  onAcceptedChange,
  onClose,
  onOpenDocument,
  onContinue,
}: {
  accepted: boolean;
  terminosUrl: string;
  onAcceptedChange: (value: boolean) => void;
  onClose: () => void;
  onOpenDocument: () => void;
  onContinue: () => void;
}) {
  return (
    <div className="fixed inset-0 z-[2200] flex items-center justify-center bg-stone-950/60 px-4 py-6 backdrop-blur-sm">
      <div className="w-full max-w-xl rounded-[32px] border border-white/80 bg-white p-6 shadow-[0_30px_90px_rgba(15,23,42,0.28)]">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-stone-500">Validación previa</p>
            <h3 className="mt-2 text-2xl font-semibold text-stone-950">Aceptas la entrega</h3>
            <p className="mt-2 text-sm text-stone-600">
              Antes de firmar la guía, confirma que revisaste el documento de Aceptación de entrega.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-2xl border border-stone-300 px-3 py-2 text-sm font-medium text-stone-700 transition hover:bg-stone-100"
          >
            Cerrar
          </button>
        </div>

        <div className="mt-6 rounded-[24px] border border-stone-200 bg-stone-50/80 p-4 text-sm text-stone-700">
          <label className="flex items-start gap-3">
            <input
              type="checkbox"
              checked={accepted}
              onChange={event => onAcceptedChange(event.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-stone-300 text-red-600 focus:ring-red-200"
            />
            <span>
              Acepto la{' '}
              {terminosUrl ? (
                <button type="button" onClick={onOpenDocument} className="font-semibold text-red-700 underline">
                  Entrega
                </button>
              ) : (
                <span className="font-semibold text-stone-700">Términos y condiciones</span>
              )}
              .
            </span>
          </label>
          {!terminosUrl && (
            <p className="mt-3 text-xs text-amber-700">
              Aún no existe un PDF configurado en el parámetro terminos_y_condiciones_url.
            </p>
          )}
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-2xl border border-stone-300 px-4 py-2 text-sm font-medium text-stone-700 transition hover:bg-stone-100"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={onContinue}
            disabled={!accepted}
            className="rounded-2xl bg-stone-950 px-4 py-2 text-sm font-semibold text-white transition hover:bg-stone-700 disabled:opacity-50"
          >
            Continuar a la firma
          </button>
        </div>
      </div>
    </div>
  );
}

function PdfPreviewModal({
  title,
  pdfUrl,
  onClose,
}: {
  title: string;
  pdfUrl: string;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-[2210] flex items-center justify-center bg-stone-950/70 px-4 py-6 backdrop-blur-sm">
      <div className="flex h-[88vh] w-full max-w-5xl flex-col overflow-hidden rounded-[32px] border border-white/80 bg-white shadow-[0_30px_90px_rgba(15,23,42,0.32)]">
        <div className="flex items-center justify-between border-b border-stone-200 px-6 py-4">
          <div>
            <h3 className="text-lg font-semibold text-stone-950">{title}</h3>
            <p className="text-sm text-stone-500">Revisa el documento y luego cierra esta ventana para continuar.</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-2xl border border-stone-300 px-4 py-2 text-sm font-medium text-stone-700 transition hover:bg-stone-100"
          >
            Cerrar
          </button>
        </div>
        <div className="min-h-0 flex-1 bg-stone-100 p-4">
          <iframe src={pdfUrl} title={title} className="h-full w-full rounded-[24px] border border-stone-200 bg-white" />
        </div>
      </div>
    </div>
  );
}

function PdfSignatureModal({
  pdfUrl,
  disabled,
  onClose,
  onSave,
}: {
  pdfUrl: string;
  disabled?: boolean;
  onClose: () => void;
  onSave: (input: PdfSignatureInput) => void;
}) {
  const pdfCanvasRef = useRef<HTMLCanvasElement>(null);
  const signatureCanvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const viewportRef = useRef<{
    convertToPdfPoint: (x: number, y: number) => [number, number];
  } | null>(null);
  const drawingRef = useRef(false);
  const strokeBoundsRef = useRef<StrokeBounds | null>(null);
  const [pdfDoc, setPdfDoc] = useState<any>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [renderError, setRenderError] = useState('');
  const [hasStroke, setHasStroke] = useState(false);
  const [isLoadingPdf, setIsLoadingPdf] = useState(true);
  const [zoom, setZoom] = useState(1);

  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    let currentTask: { destroy?: () => void } | null = null;
    const abortController = new AbortController();

    async function load() {
      setIsLoadingPdf(true);
      setPdfDoc(null);
      setRenderError('');
      try {
        const response = await fetch(pdfUrl, {
          method: 'GET',
          cache: 'no-store',
          signal: abortController.signal,
        });
        if (!response.ok) {
          throw new Error('No se pudo descargar la guía PDF desde MinIO.');
        }

        const pdfBytes = new Uint8Array(await response.arrayBuffer());
        const task = getDocument({
          data: pdfBytes,
          useWorkerFetch: false,
          isEvalSupported: false,
        });
        currentTask = task;
        const nextPdfDoc = await task.promise;
        if (cancelled) return;
        setPdfDoc(nextPdfDoc);
        setPageCount(nextPdfDoc.numPages);
        setPageNumber(1);
        setZoom(1);
      } catch (error: unknown) {
        if (cancelled) return;
        if ((error as { name?: string })?.name === 'AbortError') {
          return;
        }
        setRenderError(
          (error as { message?: string })?.message ??
            'No se pudo abrir la guía PDF para firmar. Verifica que la URL de MinIO permita descarga desde el navegador.',
        );
      } finally {
        if (!cancelled) {
          setIsLoadingPdf(false);
        }
      }
    }

    void load();

    return () => {
      cancelled = true;
      abortController.abort();
      currentTask?.destroy?.();
    };
  }, [pdfUrl]);

  useEffect(() => {
    let cancelled = false;

    async function renderPage() {
      if (!pdfDoc) return;
      setIsLoadingPdf(true);

      const page = await pdfDoc.getPage(pageNumber);
      if (cancelled) return;

      const containerWidth = Math.max((containerRef.current?.clientWidth ?? 1200) - 80, 640);
      const baseViewport = page.getViewport({ scale: 1 });
      const scale = (containerWidth / baseViewport.width) * zoom;
      const viewport = page.getViewport({ scale });
      viewportRef.current = viewport;

      const pdfCanvas = pdfCanvasRef.current;
      const overlayCanvas = signatureCanvasRef.current;
      if (!pdfCanvas || !overlayCanvas) return;

      const ratio = window.devicePixelRatio || 1;
      pdfCanvas.width = Math.floor(viewport.width * ratio);
      pdfCanvas.height = Math.floor(viewport.height * ratio);
      pdfCanvas.style.width = `${viewport.width}px`;
      pdfCanvas.style.height = `${viewport.height}px`;

      overlayCanvas.width = Math.floor(viewport.width * ratio);
      overlayCanvas.height = Math.floor(viewport.height * ratio);
      overlayCanvas.style.width = `${viewport.width}px`;
      overlayCanvas.style.height = `${viewport.height}px`;

      const pdfContext = pdfCanvas.getContext('2d');
      const overlayContext = overlayCanvas.getContext('2d');
      if (!pdfContext || !overlayContext) return;

      pdfContext.setTransform(1, 0, 0, 1, 0, 0);
      pdfContext.scale(ratio, ratio);
      await page.render({ canvasContext: pdfContext, viewport }).promise;

      overlayContext.setTransform(1, 0, 0, 1, 0, 0);
      overlayContext.clearRect(0, 0, overlayCanvas.width, overlayCanvas.height);
      overlayContext.scale(ratio, ratio);
      overlayContext.lineWidth = 2.6;
      overlayContext.lineCap = 'round';
      overlayContext.lineJoin = 'round';
      overlayContext.strokeStyle = '#0f172a';
      overlayContext.fillStyle = '#0f172a';
      strokeBoundsRef.current = null;
      setHasStroke(false);
      setIsLoadingPdf(false);
    }

    void renderPage().catch((error: unknown) => {
      if (cancelled) return;
      setRenderError((error as { message?: string })?.message ?? 'No se pudo renderizar la guía PDF.');
      setIsLoadingPdf(false);
    });

    return () => {
      cancelled = true;
    };
  }, [pdfDoc, pageNumber, zoom]);

  const resetCanvas = useCallback(() => {
    const canvas = signatureCanvasRef.current;
    const context = canvas?.getContext('2d');
    if (!canvas || !context) return;
    context.setTransform(1, 0, 0, 1, 0, 0);
    context.clearRect(0, 0, canvas.width, canvas.height);
    const ratio = window.devicePixelRatio || 1;
    context.scale(ratio, ratio);
    context.lineWidth = 2.6;
    context.lineCap = 'round';
    context.lineJoin = 'round';
    context.strokeStyle = '#0f172a';
    context.fillStyle = '#0f172a';
    strokeBoundsRef.current = null;
    setHasStroke(false);
  }, []);

  const updateStrokeBounds = useCallback((point: { x: number; y: number }) => {
    const padding = 6;
    const nextBounds = {
      left: Math.max(0, point.x - padding),
      top: Math.max(0, point.y - padding),
      right: point.x + padding,
      bottom: point.y + padding,
    };
    const currentBounds = strokeBoundsRef.current;

    strokeBoundsRef.current = currentBounds
      ? {
          left: Math.min(currentBounds.left, nextBounds.left),
          top: Math.min(currentBounds.top, nextBounds.top),
          right: Math.max(currentBounds.right, nextBounds.right),
          bottom: Math.max(currentBounds.bottom, nextBounds.bottom),
        }
      : nextBounds;
  }, []);

  function getPoint(event: ReactPointerEvent<HTMLCanvasElement>) {
    const canvas = signatureCanvasRef.current;
    if (!canvas) {
      return { x: 0, y: 0 };
    }

    const rect = canvas.getBoundingClientRect();
    return {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };
  }

  function startDrawing(event: ReactPointerEvent<HTMLCanvasElement>) {
    if (disabled) return;

    const canvas = signatureCanvasRef.current;
    const context = canvas?.getContext('2d');
    if (!canvas || !context) return;

    const point = getPoint(event);
    drawingRef.current = true;
    context.beginPath();
    context.moveTo(point.x, point.y);
    context.lineTo(point.x + 0.01, point.y + 0.01);
    context.stroke();
    updateStrokeBounds(point);
    setHasStroke(true);
  }

  function draw(event: ReactPointerEvent<HTMLCanvasElement>) {
    if (disabled || !drawingRef.current) return;

    const canvas = signatureCanvasRef.current;
    const context = canvas?.getContext('2d');
    if (!canvas || !context) return;

    const point = getPoint(event);
    context.lineTo(point.x, point.y);
    context.stroke();
    updateStrokeBounds(point);
    setHasStroke(true);
  }

  function stopDrawing() {
    if (!drawingRef.current) return;

    drawingRef.current = false;
  }

  function handleSave() {
    const canvas = signatureCanvasRef.current;
    const bounds = strokeBoundsRef.current;
    const viewport = viewportRef.current;
    if (!canvas || !hasStroke || !bounds || !viewport) return;

    const width = Math.max(1, bounds.right - bounds.left);
    const height = Math.max(1, bounds.bottom - bounds.top);
    const rect = canvas.getBoundingClientRect();
    const scaleX = rect.width > 0 ? canvas.width / rect.width : 1;
    const scaleY = rect.height > 0 ? canvas.height / rect.height : 1;
    const sourceX = Math.max(0, Math.floor(bounds.left * scaleX));
    const sourceY = Math.max(0, Math.floor(bounds.top * scaleY));
    const sourceWidth = Math.max(1, Math.ceil(width * scaleX));
    const sourceHeight = Math.max(1, Math.ceil(height * scaleY));

    const croppedCanvas = document.createElement('canvas');
    croppedCanvas.width = sourceWidth;
    croppedCanvas.height = sourceHeight;

    const croppedContext = croppedCanvas.getContext('2d');
    if (!croppedContext) return;

    croppedContext.drawImage(
      canvas,
      sourceX,
      sourceY,
      sourceWidth,
      sourceHeight,
      0,
      0,
      sourceWidth,
      sourceHeight,
    );

    const pdfCorners = [
      viewport.convertToPdfPoint(bounds.left, bounds.top),
      viewport.convertToPdfPoint(bounds.right, bounds.top),
      viewport.convertToPdfPoint(bounds.left, bounds.bottom),
      viewport.convertToPdfPoint(bounds.right, bounds.bottom),
    ];
    const pdfXs = pdfCorners.map(([x]) => x);
    const pdfYs = pdfCorners.map(([, y]) => y);

    onSave({
      signatureContent: croppedCanvas.toDataURL('image/png'),
      pageNumber,
      pdfX: Math.min(...pdfXs),
      pdfY: Math.min(...pdfYs),
      pdfWidth: Math.max(...pdfXs) - Math.min(...pdfXs),
      pdfHeight: Math.max(...pdfYs) - Math.min(...pdfYs),
    });
  }

  return (
    <div className="fixed inset-0 z-[2220] flex h-screen w-screen flex-col bg-stone-100">
      <div className="flex min-h-0 flex-1 flex-col bg-stone-100">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-stone-300 bg-white px-5 py-4 shadow-sm md:px-8">
          <div>
            <h4 className="text-lg font-semibold text-stone-950">Firmar guía PDF en vista completa</h4>
            <p className="text-sm text-stone-500">Se muestra solo la guía para que puedas firmarla sin el mapa ni otros paneles detrás.</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-2xl border border-stone-300 px-4 py-2 text-sm font-medium text-stone-700 transition hover:bg-stone-100"
          >
            Cerrar
          </button>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-stone-300 bg-white px-5 py-3 text-sm md:px-8">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPageNumber(current => Math.max(1, current - 1))}
              disabled={disabled || pageNumber <= 1}
              className="rounded-2xl border border-stone-300 px-3 py-2 text-stone-700 transition hover:bg-stone-100 disabled:opacity-50"
            >
              Pagina anterior
            </button>
            <span className="text-stone-600">Pagina {pageNumber} de {pageCount}</span>
            <button
              type="button"
              onClick={() => setPageNumber(current => Math.min(pageCount, current + 1))}
              disabled={disabled || pageNumber >= pageCount}
              className="rounded-2xl border border-stone-300 px-3 py-2 text-stone-700 transition hover:bg-stone-100 disabled:opacity-50"
            >
              Pagina siguiente
            </button>
            <button
              type="button"
              onClick={() => setZoom(current => Math.max(0.7, Number((current - 0.1).toFixed(2))))}
              disabled={disabled || zoom <= 0.7}
              className="rounded-2xl border border-stone-300 px-3 py-2 text-stone-700 transition hover:bg-stone-100 disabled:opacity-50"
            >
              Zoom -
            </button>
            <span className="text-stone-600">{Math.round(zoom * 100)}%</span>
            <button
              type="button"
              onClick={() => setZoom(current => Math.min(2.2, Number((current + 0.1).toFixed(2))))}
              disabled={disabled || zoom >= 2.2}
              className="rounded-2xl border border-stone-300 px-3 py-2 text-stone-700 transition hover:bg-stone-100 disabled:opacity-50"
            >
              Zoom +
            </button>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={resetCanvas}
              disabled={disabled}
              className="rounded-2xl border border-stone-300 px-4 py-2 text-sm font-medium text-stone-700 transition hover:bg-stone-100 disabled:opacity-50"
            >
              Limpiar firma
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={disabled || !hasStroke}
              className="rounded-2xl bg-stone-950 px-4 py-2 text-sm font-semibold text-white transition hover:bg-stone-700 disabled:opacity-50"
            >
              Guardar firma en PDF
            </button>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-hidden bg-[linear-gradient(180deg,rgba(15,23,42,0.08),rgba(15,23,42,0.02))] p-3 md:p-5">
          {renderError ? (
            <div className="mx-auto max-w-3xl rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {renderError}
            </div>
          ) : (
            <div ref={containerRef} className="h-full overflow-auto rounded-[24px] border border-stone-300/70 bg-stone-800/90 p-3 md:p-6">
              <div className="relative mx-auto w-fit rounded-[24px] bg-white p-3 shadow-[0_20px_60px_rgba(15,23,42,0.3)]">
                {isLoadingPdf && (
                  <div className="absolute inset-3 z-20 flex items-center justify-center rounded-[18px] bg-white/75 backdrop-blur-sm">
                    <div className="rounded-2xl border border-stone-200 bg-white px-4 py-3 text-sm font-medium text-stone-600 shadow-sm">
                      Cargando PDF...
                    </div>
                  </div>
                )}
                <canvas ref={pdfCanvasRef} className="relative z-0 block rounded-[18px] bg-white" />
                <canvas
                  ref={signatureCanvasRef}
                  onPointerDown={startDrawing}
                  onPointerMove={draw}
                  onPointerUp={stopDrawing}
                  onPointerLeave={stopDrawing}
                  className={`absolute inset-3 z-10 cursor-crosshair touch-none rounded-[18px] bg-transparent ${disabled ? 'opacity-70' : ''}`}
                />
                <div className="pointer-events-none absolute bottom-6 left-6 z-20 rounded-full bg-stone-950/80 px-3 py-1.5 text-xs font-medium text-white shadow-lg">
                  El PDF queda visible debajo de la firma
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}